/**
 * TriSchach Main Entry Point - TypeScript
 * Main application logic, UI handling, AI worker management, replay system
 */

import {
  BoardRenderer,
  FACTION_COLORS,
  FACTION,
  generateBoard,
} from "./board.ts";
import type { Faction, IGame, GameState } from "./types.ts";
import { retryImport } from "./lazy-retry.ts";
import type { CoachModule, RpsBuckets } from "./coach-loader";
import { applySkin, loadSkinId, saveSkinId, SKINS } from "./skins.ts";
import { GAME_STATE, PROMOTION_CHOICES } from "./game-constants.ts";
import type { Game, GameResult } from "./game.ts";
import { loadGame, createGame } from "./game-loader.ts";
import {
  calculateBestMove,
  calculateBestMoveParallel,
  evaluateBoard,
  setAIDepth,
  getAIDepth,
  getAllActions,
  setAIPersonality,
  getPersonality,
  getAIPersonalities,
  buildOpeningBook,
  // Pondering
  startPondering,
  stopPondering,
  isPondering,
} from "./ai.ts";
import {
  learnFromGame,
  loadLearnedDataFromStorage,
  saveLearnedDataToStorage,
  loadOpeningBook,
  loadLearnedDataFromFile,
} from "./opening-book.ts";
import { sounds } from "./sounds.ts";
import type { ReplayController, ReplayStateSnapshot } from "./replay.ts";
import { loadReplayModule } from "./replay-loader.ts";
import {
  loadGameStats,
  recordGameResult,
  resetGameStats,
  type GameMode,
} from "./game-stats.ts";
import type { Puzzle, PuzzleMove, PuzzleState } from "./puzzle.ts";
import { loadPuzzleModule, puzzleApi } from "./puzzle-loader.ts";
import {
  getTutorialSteps,
  markTutorialDone,
  shouldShowTutorialOnStartup,
  resetTutorial,
} from "./tutorial.ts";
import { loadAnalysisModule } from "./analysis-loader.ts";
import { Hex } from "./hex.ts";
import { isSoloAiTurn, parseHumanFaction } from "./solo.ts";
import { Piece, PIECE_TYPE } from "./pieces.ts";
import {
  logFeedbackClick,
  logFeedbackSubmit,
  generateFeedbackDraft,
  summarizeFeedback,
  resetFeedback,
} from "./game-feedback.ts";

// ─── Global Type Augmentation ────────────────────────────────────────

declare global {
  interface Window {
    replayController?: ReplayController;
    game: Game;
    renderer: BoardRenderer;
    __trischachTestPiece: typeof Piece;
    __trischachTestTypes: {
      PIECE_TYPE: typeof PIECE_TYPE;
      FACTION: typeof FACTION;
    };
    __trischachTestHex: typeof Hex;
  }
}

// ─── Security: HTML escaping for dynamic values rendered via innerHTML ──
// All externally-sourced data (puzzle SAN strings, captured-piece symbols
// from loaded game files) must be escaped before being interpolated into an
// innerHTML template, otherwise a crafted .tspn/.json file is a stored-XSS
// vector. Internal enums (FACTION_COLORS, PIECE_TYPE names) are safe.

function escapeHtml(value: unknown): string {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// ─── Settings Persistence (localStorage) ────────────────────────────────

// ─── Lazy-Module-Chunks (nicht in main.js) ─────────────────────────────
let rpsPuzzleModule: typeof import("./rps-puzzle.ts") | null = null;
let coachModule: typeof import("./coach.ts") | null = null;

const STORAGE_KEY = "trischach-settings";

interface GameSettings {
  rpsEnabled: boolean;
  soundEnabled: boolean;
  aiDepth: 1 | 2 | 3 | 4;
  boardRotation: number;
  autoBattle: boolean;
  aiPersonality: "balanced" | "aggressive" | "defensive" | "tactical";
  autoQueen?: boolean;
  animationsEnabled?: boolean;
  /** Faction the human plays. Null is hotseat (everyone is human). */
  humanFaction?: Faction | null;
  // Auto-Battle Learning settings
  abGames?: number;
  abDepth?: number;
  abMaxMoves?: number;
  abPersonalities?: string[];
}

const DEFAULT_SETTINGS: GameSettings = {
  rpsEnabled: true,
  soundEnabled: true,
  aiDepth: 3,
  boardRotation: 0,
  autoBattle: false,
  aiPersonality: "balanced",
  autoQueen: false,
  animationsEnabled: true,
  abGames: 100,
  abDepth: 4,
  abMaxMoves: 300,
  abPersonalities: ["balanced", "aggressive", "defensive", "tactical"],
};

const depthNames: Record<number, string> = {
  1: "Leicht",
  2: "Mittel",
  3: "Schwer",
  4: "Extrem",
};

function loadSettings(): GameSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
    }
  } catch (e) {
    console.warn("Failed to load settings:", e);
  }
  return { ...DEFAULT_SETTINGS };
}

function saveSettings(settings: GameSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (e) {
    console.warn("Failed to save settings:", e);
  }
}

// ─── DOM Elements ───────────────────────────────────────────────────

const svg = document.getElementById("board-svg") as unknown as SVGSVGElement;
const statusEl = document.getElementById("status") as HTMLElement;
const turnEl = document.getElementById("turn-indicator") as HTMLElement;
const coachStripEl = document.getElementById(
  "coach-strip",
) as HTMLElement | null;

/** Last select's RPS buckets — used for disadvantage confirm + hex titles */
let lastRpsAttacks: RpsBuckets | null = null;
const rpsInfoEl = document.getElementById("rps-info") as HTMLElement;
const promotionOverlay = document.getElementById(
  "promotion-overlay",
) as HTMLElement;
const puzzleOverlay = document.getElementById("puzzle-overlay") as HTMLElement;

/** Show the statistics dashboard (reuses the puzzle overlay styling). */
async function showStatsDashboard(): Promise<void> {
  // Statistik braucht die Puzzle-Statistiken — Modul hier nachladen.
  await loadPuzzleModule();
  const gs = loadGameStats();
  const ps = puzzleApi().getPuzzleSessionStats();
  const streak = puzzleApi().getPuzzleStreak();
  const pct = (a: number, b: number) =>
    b > 0 ? `${Math.round((a / b) * 100)}%` : "–";
  const factionLine = (name: string, wins: number) =>
    `<div class="puzzle-info-item"><strong>${name}:</strong> ${wins}</div>`;

  puzzleOverlay.innerHTML = `
      <div class="puzzle-box">
        <div class="puzzle-header">
          <h2 class="puzzle-title">📊 Statistiken</h2>
          <button class="puzzle-close-btn" id="stats-close-btn" title="Schließen" aria-label="Schließen">✕</button>
        </div>

        <div class="puzzle-info" id="stats-games">
          <div class="puzzle-info-item"><strong>Partien:</strong> ${gs.totalGames}</div>
          <span>🤖 Auto-Battle: <strong>${gs.autoGames}</strong></span>
          <span>👤 Manuell: <strong>${gs.manualGames}</strong></span>
        </div>
        <div class="puzzle-streak">
          <span>🔥 Feuer: <strong>${gs.winsFire}</strong></span>
          <span>💧 Wasser: <strong>${gs.winsWater}</strong></span>
          <span>🌿 Natur: <strong>${gs.winsNature}</strong></span>
          <span>🤝 Remis: <strong>${gs.draws}</strong></span>
        </div>
        ${
          gs.manualGames > 0
            ? `<div class="puzzle-info"><div class="puzzle-info-item"><strong>Manuelle Siege:</strong></div>
               ${factionLine("🔥", gs.manualWins.fire ?? 0)}
               ${factionLine("💧", gs.manualWins.water ?? 0)}
               ${factionLine("🌿", gs.manualWins.nature ?? 0)}</div>`
            : ""
        }

        <div class="puzzle-info" id="stats-puzzles">
          <div class="puzzle-info-item"><strong>Puzzles:</strong></div>
          <span>✅ Gelöst: <strong>${ps.solved}</strong>/${ps.attempts} (${pct(ps.solved, ps.attempts)})</span>
          <span>💡 Hinweise: <strong>${ps.hintsUsed}</strong></span>
          <span>🔥 Daily-Streak: <strong>${streak.current}</strong> (Best: <strong>${streak.best}</strong>)</span>
        </div>

        <div class="puzzle-info" id="stats-feedback-section">
          <!-- Feedback-Zähler wird per JS befüllt -->
        </div>

        <div class="puzzle-actions">
          <button class="puzzle-btn secondary" id="stats-reset-btn">🗑 Zurücksetzen</button>
          <button class="puzzle-btn secondary" id="stats-feedback-reset-btn">↺ Feedback zurücksetzen</button>
          <button class="puzzle-btn secondary" id="stats-feedback-btn">💬 Feedback</button>
        </div>
      </div>
    `;
  puzzleOverlay.classList.add("visible");

  const fb = summarizeFeedback();
  const fbSec = document.getElementById("stats-feedback-section");
  if (fbSec) {
    fbSec.innerHTML = `
      <div class="puzzle-info-item"><strong>Feedback:</strong> ${fb.buttonClicks} Klicks, ${fb.submits} Abgaben${fb.ratingCount > 0 ? ` (Ø ${Math.round(fb.ratingSum / fb.ratingCount)}/${fb.ratingCount})` : ""}</div>
    `;
  }

  document.getElementById("stats-close-btn")?.addEventListener("click", () => {
    puzzleOverlay.classList.remove("visible");
  });
  document.getElementById("stats-reset-btn")?.addEventListener("click", () => {
    resetGameStats();
    resetFeedback();
    showStatsDashboard();
  });
  document
    .getElementById("stats-feedback-reset-btn")
    ?.addEventListener("click", () => {
      resetFeedback();
      showStatsDashboard();
    });
  document
    .getElementById("stats-feedback-btn")
    ?.addEventListener("click", () => {
      logFeedbackClick("stats-dashboard");
      showFeedbackForm();
    });
}

// ─── Feedback Formular ────────────────────────────────────────────────

function showFeedbackForm(): void {
  puzzleOverlay.innerHTML = `
    <div class="puzzle-box">
      <div class="puzzle-header">
        <h2 class="puzzle-title">💬 Feedback</h2>
        <button class="puzzle-close-btn" id="feedback-close-btn" title="Schließen" aria-label="Schließen">✕</button>
      </div>

      <div class="puzzle-info">
        <p class="feedback-intro">
          Wie findest du trischach? Dein Feedback hilft, das Spiel zu verbessern.
          Kein Account nötig — alles bleibt lokal, bis du den fertigen Text
          selbst auf GitHub posten möchtest.
        </p>
      </div>

      <div class="feedback-form">
        <label for="feedback-rating">Bewertung (1–5):</label>
        <div class="feedback-stars" id="feedback-stars">
          <button data-rating="1" class="feedback-star" aria-label="1 Stern">★</button>
          <button data-rating="2" class="feedback-star" aria-label="2 Sterne">★</button>
          <button data-rating="3" class="feedback-star" aria-label="3 Sterne">★</button>
          <button data-rating="4" class="feedback-star" aria-label="4 Sterne">★</button>
          <button data-rating="5" class="feedback-star" aria-label="5 Sterne">★</button>
        </div>
        <div class="feedback-rating-label" id="feedback-rating-label">Noch keine Bewertung</div>
      </div>

      <div class="feedback-form">
        <label for="feedback-text">Dein Feedback:</label>
        <textarea
          id="feedback-text"
          class="feedback-textarea"
          rows="5"
          placeholder="Was gefällt dir? Was könnte besser sein?"
        ></textarea>
      </div>

      <div class="puzzle-actions">
        <button class="puzzle-btn secondary" id="feedback-show-draft-btn">📋 Vorschau anzeigen</button>
        <button class="puzzle-btn secondary" id="feedback-back-btn">← Zurück zum Dashboard</button>
      </div>
    </div>
  `;
  puzzleOverlay.classList.add("visible");

  let selectedRating = 0;

  const stars = document.querySelectorAll<HTMLButtonElement>(".feedback-star");
  const ratingLabel = document.getElementById("feedback-rating-label")!;
  const textArea = document.getElementById(
    "feedback-text",
  ) as HTMLTextAreaElement | null;
  const closeBtn = document.getElementById("feedback-close-btn")!;
  const showDraftBtn = document.getElementById("feedback-show-draft-btn")!;
  const backBtn = document.getElementById("feedback-back-btn")!;

  const updateStars = (): void => {
    stars.forEach((star) => {
      const r = Number(star.dataset.rating);
      if (r <= selectedRating) {
        star.classList.add("feedback-star--active");
      } else {
        star.classList.remove("feedback-star--active");
      }
    });
    ratingLabel.textContent =
      selectedRating > 0 ? `${selectedRating} von 5` : "Noch keine Bewertung";
  };

  stars.forEach((star) => {
    star.addEventListener("click", () => {
      selectedRating = Number(star.dataset.rating);
      updateStars();
    });
  });

  closeBtn.addEventListener("click", () => {
    puzzleOverlay.classList.remove("visible");
  });

  backBtn.addEventListener("click", () => {
    puzzleOverlay.classList.remove("visible");
    showStatsDashboard();
  });

  showDraftBtn.addEventListener("click", () => {
    const text = textArea?.value.trim() ?? "";
    if (selectedRating === 0 && text === "") return;
    const draft = generateFeedbackDraft(selectedRating || 3, text);
    logFeedbackSubmit(selectedRating || 3, text, draft);
    showFeedbackThankYou(draft, selectedRating || 3, text);
  });
}

function showFeedbackThankYou(
  draft: string,
  rating: number,
  text: string,
): void {
  puzzleOverlay.innerHTML = `
    <div class="puzzle-box">
      <div class="puzzle-header">
        <h2 class="puzzle-title">💬 Danke!</h2>
        <button class="puzzle-close-btn" id="feedback-thankyou-close-btn" title="Schließen" aria-label="Schließen">✕</button>
      </div>

      <div class="feedback-thankyou">
        <p>Dein Feedback-Entwurf ist fertig. Kopiere den Text und poste ihn auf
        GitHub, wenn du möchtest. Wir haben ihn nicht automatisch gesendet —
        das bleibt bei dir.</p>
      </div>

      <div class="feedback-draft-box">
        <pre id="feedback-draft-text" class="feedback-draft-text">${escapeHtml(draft)}</pre>
      </div>

      <div class="puzzle-actions">
        <button class="puzzle-btn" id="feedback-copy-btn">📋 Kopieren</button>
        <button class="puzzle-btn secondary" id="feedback-edit-btn">✏️ Bearbeiten</button>
        <button class="puzzle-btn secondary" id="feedback-done-btn">Fertig</button>
      </div>
    </div>
  `;
  puzzleOverlay.classList.add("visible");

  const closeBtn = document.getElementById("feedback-thankyou-close-btn")!;
  const copyBtn = document.getElementById("feedback-copy-btn")!;
  const editBtn = document.getElementById("feedback-edit-btn")!;
  const doneBtn = document.getElementById("feedback-done-btn")!;

  closeBtn.addEventListener("click", () => {
    puzzleOverlay.classList.remove("visible");
  });

  copyBtn.addEventListener("click", async () => {
    try {
      await navigator.clipboard.writeText(draft);
      copyBtn.textContent = "✓ Kopiert!";
      setTimeout(() => {
        copyBtn.textContent = "📋 Kopieren";
      }, 1500);
    } catch {
      const pre = document.getElementById("feedback-draft-text")!;
      const range = document.createRange();
      range.selectNodeContents(pre);
      const selection = window.getSelection();
      if (selection) selection.removeAllRanges();
      selection?.addRange(range);
    }
  });

  editBtn.addEventListener("click", () => {
    // Zurück zum Formular — Zustand geht verloren, aber das ist okay für einen
    // ersten Entwurf. Nutzer kann neu eingeben.
    showFeedbackForm();
  });

  doneBtn.addEventListener("click", () => {
    puzzleOverlay.classList.remove("visible");
    showStatsDashboard();
  });
}

const tutorialOverlay = document.getElementById(
  "tutorial-overlay",
) as HTMLElement | null;

const restartBtn = document.getElementById("restart-btn") as HTMLButtonElement;
const autoBattleBtn = document.getElementById(
  "auto-battle-btn",
) as HTMLButtonElement;
const rpsToggle = document.getElementById("rps-toggle") as HTMLInputElement;
const soundToggle = document.getElementById("sound-toggle") as HTMLInputElement;
const rotateBtn = document.getElementById("rotate-btn") as HTMLButtonElement;
const moveLogEl = document.getElementById("move-log") as HTMLElement;

const saveBtn = document.getElementById("save-btn") as HTMLButtonElement;
const loadBtn = document.getElementById("load-btn") as HTMLButtonElement;
const copyBtn = document.getElementById("copy-btn") as HTMLButtonElement;
const puzzleBtn = document.getElementById("puzzle-btn") as HTMLButtonElement;
const settingsBtn = document.getElementById(
  "settings-btn",
) as HTMLButtonElement;
const settingsOverlay = document.getElementById(
  "settings-overlay",
) as HTMLElement;
const settingsCloseBtn = document.getElementById(
  "settings-close",
) as HTMLButtonElement;
const settingsTabs = document.querySelectorAll(".settings-tab");
const settingsPanels = document.querySelectorAll(".settings-panel");
const darkModeBtn = document.getElementById(
  "darkmode-btn",
) as HTMLButtonElement;

const fileInput = document.createElement("input");
fileInput.type = "file";
fileInput.accept = ".tspn,text/plain";
fileInput.style.display = "none";
document.body.appendChild(fileInput);

const personalitySelect = document.getElementById(
  "personality-select",
) as HTMLSelectElement;

const renderer = new BoardRenderer(svg);
const game = await createGame();

// Record finished games for the statistics dashboard. Auto-Battle games are
// tagged "auto"; every other finished game counts as human-played.
let lastMoveCountAtGameStart = 0;
game.onGameOver = (winner) => {
  recordGameResult({
    winner,
    mode: (isAutoBattleActive() ? "auto" : "manual") as GameMode,
    moves: Math.max(0, game.moveHistory.length - lastMoveCountAtGameStart),
  });
  lastMoveCountAtGameStart = game.moveHistory.length;
};

// Expose the game + renderer instances for E2E tests / debugging (read-only
// inspection and scripted setup of edge-case positions, e.g. driving a pawn
// into the promotion zone without playing six moves by hand).
window.game = game;
window.renderer = renderer;

// Expose engine constructors/values for E2E tests that script edge-case
// positions (e.g. building a real Piece to drive promotion). Reads only.
window.__trischachTestPiece = Piece;
window.__trischachTestTypes = {
  PIECE_TYPE,
  FACTION,
};
window.__trischachTestHex = Hex;

// ─── AI Worker ──────────────────────────────────────────────────────

interface WorkerMove {
  pieceId: string;
  targetQ: number;
  targetR: number;
  moveType: string;
  rps: string;
}

interface GameStateForWorker {
  pieces: Array<{
    id: string;
    type: string;
    faction: string;
    pos: { q: number; r: number };
    symbol: string;
    alive: boolean;
    hasMoved: boolean;
  }>;
  currentFactionIdx: number;
  currentFaction: string;
  state: string;
  eliminatedFactions: string[];
  rpsEnabled: boolean;
  capturedPieces: Record<string, string[]>;
  _halfmoveClock: number;
}

let aiWorker: Worker | null = null;
let workerReady = false;
let pendingWorkerCallback: ((move: WorkerMove | null) => void) | null = null;

// Worker pool for parallel root-move search. A single worker is kept for
// pondering/legacy; the pool is used for the split search. Sized to the
// hardware concurrency (capped) so we don't oversubscribe the machine.
const PARALLEL_WORKERS = Math.max(
  1,
  Math.min(
    4,
    typeof navigator !== "undefined" ? navigator.hardwareConcurrency || 2 : 2,
  ),
);
let workerPool: Worker[] = [];
let poolCallCounter = 0;
let cbmwCallCounter = 0;

// Detect a non-browser test DOM (happy-dom under vitest). Its `Worker` stub
// tries to fetch/compile a module worker from the `new URL(...)` specifier and
// hangs the test run, so we must skip real workers there and let the
// main-thread fallback handle AI moves. happy-dom exposes a `happyDOM`
// global on the window, which is a reliable in-browser-safe signal (no
// `process` reference needed, so this also parses cleanly under ESLint).
const IS_TEST_DOM =
  typeof globalThis !== "undefined" &&
  (globalThis as { happyDOM?: unknown }).happyDOM !== undefined;

function spawnWorker(): Worker | null {
  if (typeof Worker === "undefined" || IS_TEST_DOM) return null;
  try {
    // Use `new URL(..., import.meta.url)` so Vite resolves the worker module
    // relative to THIS module (js/main.ts) instead of the page base. A bare
    // "./ai-worker.js" is resolved relative to the document URL: in a dev
    // server that hits the SPA fallback and returns index.html (not JS),
    // which makes the module worker crash with an empty error event and
    // silently forces every AI move onto the blocking main thread — freezing
    // Auto-Battle after the first move. The `new URL` form resolves to
    // /js/ai-worker.ts in dev and is correctly bundled to the hashed worker
    // asset in a production build.
    let w: Worker;
    try {
      w = new Worker(new URL("./ai-worker.ts", import.meta.url), {
        type: "module",
      });
    } catch {
      // Fallback for environments where the URL-constructor form is not
      // supported (older bundlers): bare specifier relative to the page.
      w = new Worker("./ai-worker.js", { type: "module" });
    }
    w.onerror = (err: ErrorEvent) => {
      console.warn("AI Worker error, falling back to main thread:", err);
    };
    w.postMessage({ type: "initBook" });
    return w;
  } catch (e) {
    // Any worker construction failure (unsupported env, module load error) →
    // gracefully fall back to the main-thread search instead of crashing.
    console.warn("Web Worker unavailable, using main thread AI:", e);
    return null;
  }
}

function initAIWorker(): void {
  try {
    const worker = spawnWorker();
    if (!worker) {
      // No worker support in this environment — AI runs on the main thread.
      aiWorker = null;
      workerPool = [];
      return;
    }
    aiWorker = worker;
    worker.onmessage = (e: MessageEvent) => {
      const { type, move, depth, score, nodes } = e.data;
      if (type === "result" && pendingWorkerCallback) {
        pendingWorkerCallback(move);
        pendingWorkerCallback = null;
      } else if (type === "progress") {
        console.log(`AI depth ${depth}: score ${score}, nodes ${nodes}`);
      } else if (type === "bookReady" || type === "ready") {
        workerReady = true;
        // This worker's ai-core copy starts at balanced. Push the saved
        // personality once its handler is listening.
        postWorkerPersonality(worker);
      } else if (type === "ponderReady") {
        // Worker pondering ready
      } else if (type === "ponderResult") {
        // Worker returned a pondered move - could use it
        console.log("Worker ponder result:", move);
      }
    };
    worker.onerror = (err: ErrorEvent) => {
      console.warn("AI Worker error, falling back to main thread:", err);
      aiWorker = null;
    };
    worker.postMessage({ type: "initBook" });

    // Build the parallel search pool (separate workers so pondering on the
    // legacy worker is never disturbed).
    for (let i = 0; i < PARALLEL_WORKERS; i++) {
      const poolWorker = spawnWorker();
      if (poolWorker) {
        poolWorker.addEventListener("message", (e: MessageEvent) => {
          const kind = (e.data as { type?: string } | null)?.type;
          if (kind === "ready" || kind === "bookReady") {
            postWorkerPersonality(poolWorker);
          }
        });
        workerPool.push(poolWorker);
      }
    }
  } catch (e) {
    console.warn("Web Worker not supported, using main thread AI");
    aiWorker = null;
    workerPool = [];
  }
}

function calculateBestMoveWorker(
  game: Game,
  faction: string,
  depthOverride?: number,
): Promise<WorkerMove | null> {
  const searchDepth = depthOverride ?? getAIDepth();
  const callId = ++cbmwCallCounter;
  return new Promise((resolve) => {
    if (!aiWorker || !workerReady) {
      // Single-thread fallback — use parallel root-splitting on the main thread
      // (degrades gracefully to iterativeDeepening for tiny positions).
      const move = calculateBestMoveParallel(
        game,
        faction as Faction,
        searchDepth,
      );
      if (move) {
        resolve({
          pieceId: move.piece.id,
          targetQ: move.target.q,
          targetR: move.target.r,
          moveType: move.type,
          rps: move.rps as string,
        });
      } else {
        resolve(null);
      }
      return;
    }

    // Real parallel search: split root moves across the worker pool.
    if (workerPool.length >= 2) {
      const callId = ++poolCallCounter;
      const gameState = serializeGameForWorker(game);
      const allActions = getAllActions(game, faction as Faction);
      if (allActions.length === 0) {
        resolve(null);
        return;
      }
      if (allActions.length === 1) {
        // Degenerate case — let the legacy worker do a full search.
        pendingWorkerCallback = resolve;
        aiWorker!.postMessage({
          type: "calculate",
          gameState,
          faction,
          personality: getPersonality(),
        });
        return;
      }

      const n = workerPool.length;
      const groups: { pieceId: string; targetQ: number; targetR: number }[][] =
        Array.from({ length: n }, () => []);
      allActions.forEach((a, i) => {
        groups[i % n]!.push({
          pieceId: a.piece.id,
          targetQ: a.target.q,
          targetR: a.target.r,
        });
      });

      let pending = n;
      let bestScore = -Infinity;
      let bestMove: WorkerMove | null = null;
      let settled = false;

      const handlers: Array<(e: MessageEvent) => void> = [];
      // Remove EVERY handler registered for this call from its assigned
      // worker. Pool workers are shared across consecutive auto-battle
      // calls (we post to all `workerPool.length` workers), so a handler
      // that is not removed leaks onto the next call and keeps decrementing
      // a stale `pending` counter (observed as negative pending=-1,-2,…),
      // corrupting that call's result. Removing only on `pending===0` is
      // insufficient because a worker assigned to call#1 may also receive
      // call#2's subset result before call#1 has settled.
      const cleanup = (): void => {
        workerPool.forEach((worker, i) => {
          if (handlers[i]) worker.removeEventListener("message", handlers[i]!);
        });
      };
      workerPool.forEach((worker, idx) => {
        const handler = (e: MessageEvent) => {
          if (e.data.type !== "subsetResult") return;
          const { score, move } = e.data;
          if (move && score > bestScore) {
            bestScore = score;
            bestMove = {
              pieceId: move.pieceId,
              targetQ: move.targetQ,
              targetR: move.targetR,
              moveType: move.moveType,
              rps: move.rps,
            };
          }
          pending--;
          if (pending <= 0 && !settled) {
            settled = true;
            cleanup();
            resolve(bestMove);
          } else if (!settled) {
          }
        };
        handlers.push(handler);
        // Use addEventListener (not onmessage assignment) so consecutive
        // auto-battle calls never overwrite each other's handlers on a
        // shared pool worker.
        worker.addEventListener("message", handler);
        worker.postMessage({
          type: "searchSubset",
          gameState,
          faction,
          depth: searchDepth,
          personality: getPersonality(),
          subset: groups[idx],
        });
      });
      return;
    }

    // Legacy single-worker path (pool unavailable).
    pendingWorkerCallback = resolve;
    const gameState = serializeGameForWorker(game);
    aiWorker!.postMessage({
      type: "calculate",
      gameState,
      faction,
      personality: getPersonality(),
    });
  });
}

/** KI-Modus for one worker. Each worker has its own ai-core copy. */
function postWorkerPersonality(worker: Worker): void {
  worker.postMessage({
    type: "setPersonality",
    personality: getPersonality(),
  });
}

function broadcastPersonality(): void {
  if (aiWorker) postWorkerPersonality(aiWorker);
  for (const worker of workerPool) postWorkerPersonality(worker);
}

function serializeGameForWorker(game: Game): GameStateForWorker {
  return {
    pieces: game.getAlivePieces().map((p) => ({
      id: p.id,
      type: p.type,
      faction: p.faction,
      pos: { q: p.pos.q, r: p.pos.r },
      symbol: p.symbol,
      alive: p.alive,
      hasMoved: p.hasMoved,
    })),
    currentFactionIdx: game.currentFactionIdx,
    currentFaction: game.currentFaction,
    state: game.state,
    eliminatedFactions: Array.from(game.eliminatedFactions),
    rpsEnabled: game.rpsEnabled,
    capturedPieces: {
      fire: game.capturedPieces[FACTION.FIRE].map((p) => p.id),
      water: game.capturedPieces[FACTION.WATER].map((p) => p.id),
      nature: game.capturedPieces[FACTION.NATURE].map((p) => p.id),
    },
    _halfmoveClock: game._halfmoveClock || 0,
  };
}

/**
 * One engine move through calculateBestMove — the same entry
 * scripts/engine-strength.ts measures. The worker pool's searchSubset path
 * always minimaxes and is a different player, so solo mode does not use it.
 * Passes the KI-Tiefe slider (getAIDepth) into the worker, which has its own
 * module copy of MAX_DEPTH.
 */
async function requestEngineMove(
  g: Game,
  faction: Faction,
): Promise<WorkerMove | null> {
  const depth = getAIDepth();
  setAIDepth(depth);
  // The worker posts "ready" after its module loads. Wait briefly so the
  // first move of a game does not fall onto the main thread and freeze the UI.
  if (aiWorker && !workerReady) {
    const start = Date.now();
    while (!workerReady && Date.now() - start < 500) {
      await new Promise((r) => setTimeout(r, 30));
    }
  }
  if (!aiWorker || !workerReady) {
    return workerMoveFromAction(calculateBestMove(g, faction));
  }
  const callId = ++engineCallId;
  const worker = aiWorker;
  return new Promise((resolve) => {
    let timer: ReturnType<typeof setTimeout>;
    const finish = (move: WorkerMove | null) => {
      clearTimeout(timer);
      worker.removeEventListener("message", handler);
      worker.removeEventListener("error", onError);
      resolve(move);
    };
    const handler = (e: MessageEvent) => {
      const data = e.data as {
        type?: string;
        callId?: number;
        move?: WorkerMove | null;
      };
      if (!data || data.type !== "result" || data.callId !== callId) return;
      finish(data.move ?? null);
    };
    // A worker that dies mid-search (or never answers) must not leave the
    // human staring at an idle board. Same entry point as the bench.
    const onError = () => {
      finish(workerMoveFromAction(calculateBestMove(g, faction)));
    };
    timer = setTimeout(() => onError(), 15000);
    worker.addEventListener("message", handler);
    worker.addEventListener("error", onError);
    worker.postMessage({
      type: "calculate",
      gameState: serializeGameForWorker(g),
      faction,
      depth,
      personality: getPersonality(),
      callId,
    });
  });
}

let engineCallId = 0;

function workerMoveFromAction(
  move: {
    piece: { id: string };
    target: { q: number; r: number };
    type: string;
    rps?: string;
  } | null,
): WorkerMove | null {
  if (!move) return null;
  return {
    pieceId: move.piece.id,
    targetQ: move.target.q,
    targetR: move.target.r,
    moveType: move.type,
    rps: String(move.rps ?? ""),
  };
}

function cancelSoloSearch(): void {
  soloGeneration += 1;
  aiMoveInFlight = false;
  soloGaveUp = false;
}

function soloGameOver(): boolean {
  return (
    game.state === GAME_STATE.GAME_OVER ||
    game.state === GAME_STATE.DRAW_REPETITION ||
    game.state === GAME_STATE.DRAW_50MOVE
  );
}

function soloUiBlocked(): boolean {
  if (game.state === GAME_STATE.PROMOTION) return true;
  for (const id of [
    "combat-overlay",
    "promotion-overlay",
    "puzzle-overlay",
    "tutorial-overlay",
  ]) {
    const el = document.getElementById(id);
    if (el?.classList.contains("visible")) return true;
  }
  const replay = document.getElementById("replay-controls");
  return replay?.style.display === "flex";
}

function maybePlaySoloMove(): void {
  if (soloGaveUp) return;
  if (
    !isSoloAiTurn({
      humanFaction,
      currentFaction: game.currentFaction,
      autoBattle: autoBattleActive,
      gameOver: soloGameOver(),
      blocked: soloUiBlocked(),
      inFlight: aiMoveInFlight,
    })
  ) {
    return;
  }
  void playSoloMove();
}

async function playSoloMove(): Promise<void> {
  if (aiMoveInFlight) return;
  const gen = soloGeneration;
  aiMoveInFlight = true;
  updateUI();
  try {
    const faction = game.currentFaction;
    const historyBefore = game.moveHistory.length;
    const move = await requestEngineMove(game, faction);
    if (gen !== soloGeneration) return;
    if (
      autoBattleActive ||
      game.currentFaction !== faction ||
      soloGameOver() ||
      soloUiBlocked()
    ) {
      return;
    }
    if (!move) {
      soloGaveUp = true;
      return;
    }
    const piece = game.pieces.find((p) => p.id === move.pieceId);
    if (!piece) {
      soloGaveUp = true;
      return;
    }
    game.handleCellClick(piece.pos);
    const target = new Hex(move.targetQ, move.targetR);
    const result = game.handleCellClick(target);
    renderer.clearHighlights();
    renderer.clearSelection();
    if (!result || !result.piece || game.moveHistory.length === historyBefore) {
      soloGaveUp = true;
      return;
    }
    soloGaveUp = false;
    const played = result.piece;
    if (result.action === "combat") {
      addToLog(result);
      if (result.promotion && game.pendingPromotion) {
        const promo = game.completePromotion("queen");
        if (promo) addToLog(promo);
      }
      showCombat(result);
      return;
    }
    if (result.action === "move") {
      sounds.playMove();
      addToLog(result);
      if (result.promotion && game.pendingPromotion) {
        const promo = game.completePromotion("queen");
        if (promo) addToLog(promo);
      }
      renderer.renderPiece(played);
    }
  } finally {
    if (gen === soloGeneration) {
      aiMoveInFlight = false;
      updateUI();
    }
  }
}

// ─── Global State ───────────────────────────────────────────────────

let autoBattleActive = false;
/** Faction the human plays. Null keeps the hotseat (all three are human). */
let humanFaction: Faction | null = null;
let soloGeneration = 0;
let aiMoveInFlight = false;
/** Stops a failed engine search from immediately scheduling itself again. */
let soloGaveUp = false;

function isAutoBattleActive(): boolean {
  return autoBattleActive;
}
let autoBattleTimer: ReturnType<typeof setTimeout> | null = null;
let currentBoardRotation = 0;

// Track opening book moves for learning
interface BookMoveRecord {
  hash: string;
  faction: Faction;
  move: { pieceId: string; targetQ: number; targetR: number };
}
let autoBattleBookMoves: BookMoveRecord[] = [];

// ─── Initialization ─────────────────────────────────────────────────

function applySettings(settings: GameSettings): void {
  game.rpsEnabled = settings.rpsEnabled;
  const rpsToggle = document.getElementById("rps-toggle") as HTMLInputElement;
  if (rpsToggle) rpsToggle.checked = settings.rpsEnabled;

  sounds.toggle(settings.soundEnabled);
  const soundToggle = document.getElementById(
    "sound-toggle",
  ) as HTMLInputElement;
  if (soundToggle) soundToggle.checked = settings.soundEnabled;

  setAIDepth(settings.aiDepth);
  const depthSlider = document.getElementById(
    "depth-slider",
  ) as HTMLInputElement;
  const depthLabel = document.getElementById("depth-label") as HTMLElement;
  if (depthSlider) depthSlider.value = String(settings.aiDepth);
  if (depthLabel)
    depthLabel.textContent = "KI: " + depthNames[settings.aiDepth];

  currentBoardRotation = settings.boardRotation % 360;
  renderer.setRotation(currentBoardRotation);

  autoBattleActive = settings.autoBattle;
  const autoBattleBtn = document.getElementById(
    "auto-battle-btn",
  ) as HTMLButtonElement;
  if (autoBattleBtn) {
    if (autoBattleActive) {
      autoBattleBtn.textContent = "⏹ Auto Battle Stoppen";
      autoBattleBtn.classList.add("active");
    } else {
      autoBattleBtn.textContent = "🤖 Auto Battle";
      autoBattleBtn.classList.remove("active");
    }
  }

  setAIPersonality(settings.aiPersonality);
  broadcastPersonality();
  const personalitySelect = document.getElementById(
    "personality-select",
  ) as HTMLSelectElement;
  if (personalitySelect) personalitySelect.value = settings.aiPersonality;

  const animationsEnabled = settings.animationsEnabled !== false;
  document.documentElement.classList.toggle(
    "animations-disabled",
    !animationsEnabled,
  );
  const animationsToggle = document.getElementById(
    "animations-toggle",
  ) as HTMLInputElement | null;
  if (animationsToggle) animationsToggle.checked = animationsEnabled;

  humanFaction = parseHumanFaction(settings.humanFaction);
  const playAs = document.getElementById("play-as") as HTMLSelectElement | null;
  if (playAs) playAs.value = humanFaction ?? "";

  updateUI();
}

function init(): void {
  renderer.render();
  game.init(renderer.cells);
  game.clearUndoStack();
  autoBattleBookMoves = []; // Clear learning tracking

  // Render initial pieces synchronously (board must be ready immediately)
  for (const p of game.getAlivePieces()) renderer.renderPiece(p);
  updateUI();

  // Load production opening book + learned data
  (async () => {
    try {
      await loadOpeningBook(); // Load compiled book from JSON
      await loadLearnedDataFromFile(); // Load auto-battle learned data from file
    } catch (e) {
      console.warn("Failed to load opening book or learned data:", e);
    }
    loadLearnedDataFromStorage(); // Merge localStorage learned data (non-fatal)
    initAIWorker();
    const settings = loadSettings();
    applySettings(settings);

    if (game.boardCells) {
      for (const [key, cell] of renderer.hexElements) {
        const c = game.boardCells.get(key);
        if (c)
          cell.polygon.setAttribute("title", `Coord: ${c.hex.q},${c.hex.r}`);
      }
    }

    updateUI();
  })();
}

function updateCoachStrip(flash = false): void {
  if (aiMoveInFlight && humanFaction) {
    if (coachStripEl) {
      coachStripEl.textContent = "KI denkt…";
      coachStripEl.className = "coach-strip coach-strip--info";
    }
    if (statusEl && game.state !== GAME_STATE.GAME_OVER) {
      statusEl.textContent = "KI denkt…";
      statusEl.style.color = "";
    }
    return;
  }
  if (!coachStripEl) return;
  if (!coachModule) {
    void loadCoachModule()
      .then(() => updateCoachStrip(flash))
      .catch(() => {});
    return;
  }
  const msg = coachApi().getCoachMessage({
    state: game.state,
    currentFaction: game.currentFaction,
    rpsEnabled: game.rpsEnabled,
    selectedPiece: game.selectedPiece,
    validMoves: game.validMoves,
    validAttacks: game.validAttacks,
    isKingInCheck: (fac) => game.isKingInCheck(fac),
    getPieceAt: (h) => game.getPieceAt(h),
  });
  coachStripEl.textContent = msg.text;
  coachStripEl.className = `coach-strip coach-strip--${msg.tone}`;
  if (flash) {
    coachStripEl.classList.remove("coach-strip--flash");
    void coachStripEl.offsetWidth;
    coachStripEl.classList.add("coach-strip--flash");
  }
  // Keep compact status in sync for accessibility
  if (statusEl && game.state !== GAME_STATE.GAME_OVER) {
    statusEl.textContent = msg.text;
    statusEl.style.color =
      msg.tone === "check" || msg.tone === "rps-bad" ? "#ff8888" : "";
  }
}

/** Native tooltips on attack hexes explaining RPS before the click. */
function applyRpsHexTitles(
  attacker: { faction: Faction } | null | undefined,
  rpsAttacks: RpsBuckets | null | undefined,
): void {
  // Reset every cell to its coord title first, then overwrite the RPS attack
  // cells. We must NOT drop the base "Coord: q,r" title (used by tooltips and
  // E2E locators) — only augment attack cells with the RPS outcome.
  for (const [, el] of renderer.hexElements) {
    const c = el.cell?.hex;
    el.polygon.setAttribute("title", `Coord: ${c?.q ?? "?"},${c?.r ?? "?"}`);
  }
  if (!attacker || !rpsAttacks || !game.rpsEnabled) return;

  const apply = (hexes: Hex[], _kind: string) => {
    for (const h of hexes) {
      const def = game.getPieceAt(h);
      if (!def) continue;
      const el = renderer.hexElements.get(h.key);
      if (!el) continue;
      el.polygon.setAttribute(
        "title",
        coachApi().rpsCaptureTitleFromPieces(
          attacker as import("./types.ts").Piece,
          def,
        ),
      );
    }
  };
  apply(rpsAttacks.advantage, "advantage");
  apply(rpsAttacks.neutral, "neutral");
  apply(rpsAttacks.disadvantage, "disadvantage");
}

function updateUI(): void {
  const f = game.currentFaction;
  const fc = FACTION_COLORS[f];
  const turnEl = document.getElementById("turn-indicator") as HTMLElement;
  const statusEl = document.getElementById("status") as HTMLElement;
  if (turnEl) {
    turnEl.textContent = fc.name;
    turnEl.style.color = fc.primary;
  }

  if (game.state === GAME_STATE.GAME_OVER) {
    // keep existing game over text
  } else {
    updateCoachStrip();
  }

  clearCheckHighlight();
  if (game.isKingInCheck(f)) {
    const king = game.pieces.find(
      (p) => p.faction === f && p.type === "king" && p.alive,
    );
    if (king) {
      const el = renderer.hexElements.get(king.pos.key);
      if (el) el.polygon.classList.add("highlight-check");
    }
  }

  // Last-move marker (from/to of the most recent played move)
  const lastResult = [...game.moveHistory]
    .reverse()
    .find(
      (r) =>
        (r.action === "move" || r.action === "combat") &&
        (r.from != null || r.to != null),
    );
  renderer.highlightLastMove(lastResult?.from, lastResult?.to);

  for (const fac of [FACTION.FIRE, FACTION.WATER, FACTION.NATURE]) {
    const el = document.getElementById(`panel-${fac}`);
    if (!el) continue;
    el.classList.toggle("eliminated", game.eliminatedFactions.has(fac));
    el.classList.toggle("solo-human", humanFaction === fac);
  }

  maybePlaySoloMove();

  const rpsInfoEl = document.getElementById("rps-info") as HTMLElement | null;
  if (game.rpsEnabled) {
    if (rpsInfoEl) rpsInfoEl.classList.remove("rps-inactive");
    document
      .querySelectorAll(".rps-hint")
      .forEach((el) => el.classList.remove("hidden"));
  } else {
    if (rpsInfoEl) rpsInfoEl.classList.add("rps-inactive");
    document
      .querySelectorAll(".rps-hint")
      .forEach((el) => el.classList.add("hidden"));
  }

  for (const fac of [FACTION.FIRE, FACTION.WATER, FACTION.NATURE]) {
    const capEl = document.getElementById(`captures-${fac}`);
    if (capEl) {
      capEl.innerHTML = game.capturedPieces[fac]
        .map(
          (p) => `<span class="captured-piece">${escapeHtml(p.symbol)}</span>`,
        )
        .join("");
    }
  }

  updateEvalBar();
}

function updateEvalBar(): void {
  const fireEval = evaluateBoard(game, FACTION.FIRE);
  const natureEval = evaluateBoard(game, FACTION.NATURE);
  const waterEval = evaluateBoard(game, FACTION.WATER);

  const minEval = Math.min(fireEval, natureEval, waterEval);
  const shifted: [number, number, number] = [
    fireEval - minEval,
    natureEval - minEval,
    waterEval - minEval,
  ];
  const maxShifted = Math.max(...shifted, 1);

  const firePct = (shifted[0] / maxShifted) * 100;
  const naturePct = (shifted[1] / maxShifted) * 100;
  const waterPct = (shifted[2] / maxShifted) * 100;

  const evalFire = document.getElementById("eval-fire") as HTMLElement;
  const evalNature = document.getElementById("eval-nature") as HTMLElement;
  const evalWater = document.getElementById("eval-water") as HTMLElement;
  if (evalFire) evalFire.style.width = firePct + "%";
  if (evalNature) evalNature.style.width = naturePct + "%";
  if (evalWater) evalWater.style.width = waterPct + "%";
}

function clearCheckHighlight(): void {
  document
    .querySelectorAll(".highlight-check")
    .forEach((el) => el.classList.remove("highlight-check"));
}

/** Assigned by initEventListeners; lets move-log entries jump into replay. */
let jumpToMoveImpl: ((moveIndex: number) => Promise<void>) | null = null;

function addToLog(result: GameResult): void {
  if (!result.piece) return;
  const moveLogEl = document.getElementById("move-log") as HTMLElement | null;
  if (!moveLogEl) return; // DOM may be gone if called from a deferred timer
  const index = game.moveHistory.length; // this result is about to be/appended
  const entry = document.createElement("div");
  entry.className = `move-entry ${result.piece.faction}`;
  entry.dataset.moveIndex = String(index);
  entry.title = "Klicken zum Anspringen";
  const pieceSpan = document.createElement("span");
  pieceSpan.className = "move-piece";
  pieceSpan.textContent = result.piece.symbol;
  const coordsSpan = document.createElement("span");
  coordsSpan.className = "move-coords";
  coordsSpan.textContent = result.notation ?? "";
  entry.append(pieceSpan, coordsSpan);
  entry.addEventListener("click", () => {
    // Set by initEventListeners once the replay scope is initialized.
    jumpToMoveImpl?.(index).catch((err) =>
      console.error("jumpToMove failed:", err),
    );
  });
  moveLogEl.appendChild(entry);
  moveLogEl.scrollTop = moveLogEl.scrollHeight;
}

// ─── Cell Click Handler ──────────────────────────────────────────────

// Hover preview: while no piece is selected, hovering one of your own pieces
// shows its legal targets subtly (desktop only — pointer devices).
renderer.onCellHover = (hex: { q: number; r: number } | null) => {
  if (hex == null || game.state !== GAME_STATE.SELECT_PIECE) {
    renderer.showMovePreview(undefined, undefined);
    return;
  }
  if (humanFaction && game.currentFaction !== humanFaction) {
    renderer.clearMovePreview();
    return;
  }
  const piece = game.getPieceAt(new Hex(hex.q, hex.r));
  if (!piece || piece.faction !== game.currentFaction || !piece.alive) {
    renderer.clearMovePreview();
    return;
  }
  const { moves, attacks } = game.getLegalMoves(piece);
  renderer.showMovePreview(moves, attacks);
};

renderer.onCellClick = (hex: { q: number; r: number }) => {
  if (aiMoveInFlight) return;
  if (humanFaction && game.currentFaction !== humanFaction) {
    soloGaveUp = false;
    maybePlaySoloMove();
    return;
  }
  const hexObj = new Hex(hex.q, hex.r);

  // Confirm suicidal RPS captures before the engine applies them
  if (
    game.state === GAME_STATE.SELECT_TARGET &&
    game.rpsEnabled &&
    game.selectedPiece &&
    coachModule &&
    coachApi().isDisadvantageHex(hexObj, lastRpsAttacks)
  ) {
    const def = game.getPieceAt(hexObj);
    const title =
      def && coachModule
        ? coachApi().rpsCaptureTitleFromPieces(game.selectedPiece, def)
        : "RPS-Nachteil";
    const ok = window.confirm(
      `${title}\n\nBei Nachteil stirbt DEINE Figur — der Verteidiger bleibt.\nTrotzdem angreifen?`,
    );
    if (!ok) {
      updateCoachStrip(true);
      return;
    }
  }

  // Soft feedback when clicking an illegal target while a piece is selected
  if (game.state === GAME_STATE.SELECT_TARGET && game.selectedPiece) {
    const isMove = game.validMoves.some((m) => m.equals(hexObj));
    const isAttack = game.validAttacks.some((a) => a.equals(hexObj));
    const isOwn = game.getPieceAt(hexObj)?.faction === game.currentFaction;
    if (!isMove && !isAttack && !isOwn) {
      if (coachStripEl) {
        coachStripEl.textContent =
          "Ungültiges Feld — wähle ein markiertes Ziel";
        coachStripEl.className =
          "coach-strip coach-strip--warn coach-strip--flash";
      }
      // still fall through to deselect via game logic
    }
  }

  const result = game.handleCellClick(hexObj);
  if (!result) return;

  renderer.clearHighlights();
  renderer.clearSelection();

  if (result.action === "select") {
    sounds.playSelect();
    renderer.selectCell(hexObj);
    renderer.highlightCells(result.moves ?? [], "highlight-move");
    lastRpsAttacks = (result.rpsAttacks as RpsBuckets) || null;
    if (game.rpsEnabled && result.rpsAttacks) {
      renderer.highlightCells(
        result.rpsAttacks.advantage ?? [],
        "highlight-attack-advantage",
      );
      renderer.highlightCells(
        result.rpsAttacks.disadvantage ?? [],
        "highlight-attack-disadvantage",
      );
      renderer.highlightCells(
        result.rpsAttacks.neutral ?? [],
        "highlight-attack",
      );
      applyRpsHexTitles(result.piece ?? game.selectedPiece, lastRpsAttacks);
    } else {
      lastRpsAttacks = null;
      applyRpsHexTitles(null, null);
      renderer.highlightCells(result.attacks ?? [], "highlight-attack");
    }
  } else if (result.action === "deselect") {
    lastRpsAttacks = null;
    applyRpsHexTitles(null, null);
  } else if (result.action === "move") {
    lastRpsAttacks = null;
    applyRpsHexTitles(null, null);
    sounds.playMove();
    addToLog(result);
    if (result.piece) renderer.renderPiece(result.piece);

    // NOTE: main-thread pondering is intentionally skipped. startPondering()
    // runs a synchronous minimax (up to depth 12) in the calling thread, which
    // freezes the UI after every human move. True pondering belongs in the AI
    // Web Worker; the Auto-Battle path already uses calculateBestMoveWorker().
    // If worker-backed pondering is added later, gate it on `aiWorker &&
    // workerReady` so it never runs on the main thread.
    // if (game.state !== GAME_STATE.GAME_OVER) { startPondering(game, game.currentFaction); }

    if (result.promotion && game.pendingPromotion) {
      showPromotion(game.pendingPromotion);
    } else {
      updateUI();
    }
  } else if (result.action === "combat") {
    lastRpsAttacks = null;
    applyRpsHexTitles(null, null);
    addToLog(result);
    showCombat(result);

    // See note above: main-thread pondering skipped to avoid UI freeze.
    // if (game.state !== GAME_STATE.GAME_OVER) { startPondering(game, game.currentFaction); }
  }

  if (
    result.inCheck &&
    result.action !== "select" &&
    result.action !== "deselect"
  ) {
    sounds.playCheck();
  }

  if (result.action === "select" || result.action === "deselect") updateUI();
};

// ─── Context Menu ────────────────────────────────────────────────────

let contextMenuPiece: Piece | null = null;

renderer.onPieceLongPress = (
  piece: Piece,
  position: { clientX: number; clientY: number },
) => {
  if (game.state === GAME_STATE.GAME_OVER) return;
  if (piece.faction !== game.currentFaction) return;

  contextMenuPiece = piece;
  showContextMenu(piece, position);
};

function showContextMenu(
  piece: Piece,
  position: { clientX: number; clientY: number },
): void {
  const existing = document.getElementById("piece-context-menu");
  if (existing) existing.remove();

  const { moves, attacks } = game.getLegalMoves(piece);
  const hasMoves = moves.length > 0 || attacks.length > 0;

  const menu = document.createElement("div");
  menu.id = "piece-context-menu";
  menu.className = "piece-context-menu";
  menu.style.left = `${position.clientX}px`;
  menu.style.top = `${position.clientY}px`;

  const header = document.createElement("div");
  header.className = "context-menu-header";
  header.textContent = `${piece.symbol} ${piece.type} (${piece.faction})`;
  menu.appendChild(header);

  const divider = document.createElement("div");
  divider.className = "context-menu-divider";
  menu.appendChild(divider);

  const addItem = (action: string, icon: string, label: string): void => {
    const btn = document.createElement("button");
    btn.className = "context-menu-item";
    btn.dataset.action = action;
    const iconSpan = document.createElement("span");
    iconSpan.className = "context-menu-icon";
    iconSpan.textContent = icon;
    const labelSpan = document.createElement("span");
    labelSpan.textContent = label;
    btn.append(iconSpan, labelSpan);
    menu.appendChild(btn);
  };

  if (hasMoves) {
    addItem("show-moves", "🎯", "Mögliche Züge anzeigen");
  }

  addItem("undo", "↩️", "Zug zurücknehmen");
  addItem("save", "💾", "Spiel speichern");
  addItem("copy", "📋", "TSPN kopieren");

  const divider2 = document.createElement("div");
  divider2.className = "context-menu-divider";
  menu.appendChild(divider2);

  const dangerBtn = document.createElement("button");
  dangerBtn.className = "context-menu-item context-menu-danger";
  dangerBtn.dataset.action = "deselect";
  const dangerIcon = document.createElement("span");
  dangerIcon.className = "context-menu-icon";
  dangerIcon.textContent = "✕";
  const dangerLabel = document.createElement("span");
  dangerLabel.textContent = "Abbrechen";
  dangerBtn.append(dangerIcon, dangerLabel);
  menu.appendChild(dangerBtn);

  document.body.appendChild(menu);
  requestAnimationFrame(() => {
    const rect = menu.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    if (rect.right > viewportWidth - 10) {
      menu.style.left = `${viewportWidth - rect.width - 10}px`;
    }
    if (rect.bottom > viewportHeight - 10) {
      menu.style.top = `${viewportHeight - rect.height - 10}px`;
    }
  });

  menu.querySelectorAll(".context-menu-item").forEach((btn) => {
    btn.addEventListener("click", () => {
      void handleContextMenuAction((btn as HTMLElement).dataset.action, piece);
      hideContextMenu();
    });
  });

  const closeOnClick = (e: MouseEvent) => {
    if (!menu.contains(e.target as Node)) {
      hideContextMenu();
      document.removeEventListener("click", closeOnClick);
    }
  };
  setTimeout(() => document.addEventListener("click", closeOnClick), 0);
}

async function handleContextMenuAction(
  action: string | undefined,
  piece: {
    id: string;
    type: string;
    faction: string;
    pos: Hex;
    symbol: string;
  },
): Promise<void> {
  if (!action) return;

  switch (action) {
    case "show-moves": {
      const selectResult = game.handleCellClick(piece.pos);
      if (selectResult && selectResult.action === "select") {
        renderer.clearHighlights();
        renderer.selectCell(piece.pos);
        renderer.highlightCells(selectResult.moves ?? [], "highlight-move");
        lastRpsAttacks = (selectResult.rpsAttacks as RpsBuckets) || null;
        if (game.rpsEnabled && selectResult.rpsAttacks) {
          renderer.highlightCells(
            selectResult.rpsAttacks.advantage ?? [],
            "highlight-attack-advantage",
          );
          renderer.highlightCells(
            selectResult.rpsAttacks.disadvantage ?? [],
            "highlight-attack-disadvantage",
          );
          renderer.highlightCells(
            selectResult.rpsAttacks.neutral ?? [],
            "highlight-attack",
          );
          applyRpsHexTitles(
            selectResult.piece ?? game.selectedPiece,
            lastRpsAttacks,
          );
        } else {
          lastRpsAttacks = null;
          applyRpsHexTitles(null, null);
          renderer.highlightCells(
            selectResult.attacks ?? [],
            "highlight-attack",
          );
        }
        updateUI();
      }
      break;
    }
    case "undo": {
      const snap = game.undo();
      if (snap) updateUI();
      break;
    }
    case "save": {
      const { downloadGame } = await loadReplayModule();
      downloadGame(
        game,
        `trischach-${new Date().toISOString().slice(0, 10)}.tspn`,
      );
      break;
    }
    case "copy": {
      navigator.clipboard
        .writeText(window.replayController?.exportTSPN() || "")
        .then(() => {
          console.log("TSPN copied to clipboard");
        });
      break;
    }
  }
}

function hideContextMenu(): void {
  const existing = document.getElementById("piece-context-menu");
  if (existing) existing.remove();
}

// ─── Auto-Battle ────────────────────────────────────────────────────

function triggerAutoMove(): void {
  if (autoBattleTimer) clearTimeout(autoBattleTimer);
  autoBattleTimer = setTimeout(async () => {
    if (!autoBattleActive || game.state === "game_over") return;

    if (game.state === "select_target" && game.selectedPiece) {
      game.handleCellClick(game.selectedPiece.pos);
    }

    if (game.eliminatedFactions.has(game.currentFaction)) {
      game._nextTurn();
      triggerAutoMove();
      return;
    }

    // Check if current position is in opening book before making move
    // Check if current position is in opening book before making move
    const {
      inBook: inBookDyn,
      pickBookMove: pickBookMoveDyn,
      boardHash: boardHashDyn,
    } = await import("./opening-book.ts");
    const wasInBook = inBookDyn(game);
    const bookMove = wasInBook ? pickBookMoveDyn(game) : null;

    // Stop pondering and get the best move found
    const ponderMove = await stopPondering();

    let action;
    if (ponderMove) {
      action = {
        pieceId: ponderMove.piece.id,
        targetQ: ponderMove.target.q,
        targetR: ponderMove.target.r,
        moveType: ponderMove.type,
        rps: ponderMove.rps,
      };
    } else {
      action = await calculateBestMoveWorker(game, game.currentFaction, 2);
    }

    if (action) {
      const piece = game.pieces.find((p) => p.id === action.pieceId);
      if (!piece) {
        triggerAutoMove();
        return;
      }

      // Record book move for learning if it was a book move
      if (wasInBook && bookMove && bookMove.piece.id === piece.id) {
        const { boardHash: boardHashDyn2 } = await import("./opening-book.ts");
        const hash = boardHashDyn2(game);
        autoBattleBookMoves.push({
          hash,
          faction: game.currentFaction,
          move: {
            pieceId: piece.id,
            targetQ: action.targetQ,
            targetR: action.targetR,
          },
        });
      }

      game.handleCellClick(piece.pos);
      const target = new Hex(action.targetQ, action.targetR);
      const result = game.handleCellClick(target);

      renderer.clearHighlights();
      renderer.clearSelection();

      if (result && result.action === "move") {
        sounds.playMove();
        addToLog(result);
        const movedPiece = result.piece;
        if (movedPiece) renderer.renderPiece(movedPiece);
        if (result.promotion) {
          const promoResult = game.completePromotion("queen");
          if (promoResult && movedPiece) {
            renderer.removePiece(movedPiece.id);
            renderer.renderPiece(movedPiece);
            addToLog(promoResult);
          }
          updateUI();

          // Main-thread pondering omitted (would freeze UI); see note in
          // handleCellClick. Auto-Battle uses the AI Web Worker via
          // calculateBestMoveWorker() for move computation.
          triggerAutoMove();
        } else {
          updateUI();

          // Main-thread pondering omitted (see note above).
          triggerAutoMove();
        }
      } else if (result && result.action === "combat") {
        addToLog(result);
        // A combat can also trigger a pawn promotion: complete it so the
        // game leaves the promotion state and Auto-Battle can continue.
        if (result.promotion) {
          const promoResult = game.completePromotion("queen");
          if (promoResult) addToLog(promoResult);
          updateUI();
        }
        showCombat(result);
      } else {
        autoBattleActive = false;
        const autoBattleBtn = document.getElementById(
          "auto-battle-btn",
        ) as HTMLButtonElement;
        if (autoBattleBtn) {
          autoBattleBtn.textContent = "🤖 Auto Battle";
          autoBattleBtn.classList.remove("active");
        }
        updateUI();
      }
    } else {
      const aliveFactions = [
        FACTION.FIRE,
        FACTION.WATER,
        FACTION.NATURE,
      ].filter((f) => !game.eliminatedFactions.has(f));
      if (aliveFactions.length <= 1) {
        game.state = "game_over";
        updateUI();
      } else {
        game._nextTurn();
        triggerAutoMove();
      }
    }
  }, 400);
}

// ─── Combat Overlay ─────────────────────────────────────────────────

function showCombat(result: GameResult): void {
  const piece = result.piece;
  const defender = result.defender;
  if (!piece || !defender) return;
  const combatOverlay = document.getElementById(
    "combat-overlay",
  ) as HTMLElement | null;
  const attColor = FACTION_COLORS[piece.faction];
  const defColor = FACTION_COLORS[defender.faction];
  const rps = result.rpsResult;

  sounds.playCombat();

  if (!combatOverlay) return; // Overlay missing (e.g. headless) — nothing to render
  combatOverlay.innerHTML = `
    <div class="combat-box">
      <div class="combat-fighters">
        <div class="fighter" style="color:${attColor.primary}">
          <span class="fighter-symbol">${piece.symbol}</span>
          <span class="fighter-name">${attColor.name}</span>
        </div>
        <div class="combat-vs">${rps === "advantage" ? ">" : "<"}</div>
        <div class="fighter" style="color:${defColor.primary}">
          <span class="fighter-symbol">${defender.symbol}</span>
          <span class="fighter-name">${defColor.name}</span>
        </div>
      </div>
      <div class="combat-result ${rps}">
        ${
          rps === "advantage"
            ? `${attColor.name} besiegt ${defColor.name}!`
            : `${defColor.name} wehrt ab! ${attColor.name} verliert!`
        }
      </div>
      ${result.elimination ? `<div class="combat-elimination">💀 ${FACTION_COLORS[result.elimination].name} ist eliminiert!</div>` : ""}
      ${result.checkmate ? `<div class="combat-checkmate">♚ Schachmatt! ${FACTION_COLORS[result.checkmate].name} ist eliminiert!</div>` : ""}
      ${result.stalemate ? `<div class="combat-stalemate">🤖 Patt! ${FACTION_COLORS[result.stalemate].name} ist eliminiert!</div>` : ""}
      ${result.inCheck && !result.checkmate ? `<div class="combat-check">⚠️ Schach!</div>` : ""}
      ${result.gameOver ? `<div class="combat-winner">🏆 ${FACTION_COLORS[result.winner_faction!].name} gewinnt!</div>` : ""}
      ${autoBattleActive && !result.gameOver ? `<button id="stop-auto-combat" class="combat-stop-btn">⏹ Auto Battle Stoppen</button>` : ""}
    </div>
  `;
  if (combatOverlay) combatOverlay.classList.add("visible");

  const stopBtn = document.getElementById("stop-auto-combat");
  if (stopBtn) {
    stopBtn.onclick = () => {
      autoBattleActive = false;
      const autoBattleBtn = document.getElementById(
        "auto-battle-btn",
      ) as HTMLButtonElement;
      if (autoBattleBtn) {
        autoBattleBtn.textContent = "🤖 Auto Battle";
        autoBattleBtn.classList.remove("active");
      }
      clearTimeout(autoBattleTimer!);
      stopBtn.remove();
    };
  }

  setTimeout(() => {
    const combatOverlay = document.getElementById(
      "combat-overlay",
    ) as HTMLElement | null;
    const boardGroup = document.getElementById("board-group");
    if (combatOverlay) combatOverlay.classList.remove("visible");
    if (boardGroup) {
      boardGroup.querySelectorAll(".piece").forEach((el) => el.remove());
    }
    renderer.pieceElements.clear();
    for (const p of game.getAlivePieces()) renderer.renderPiece(p);
    updateUI();
    if (result.elimination) sounds.playElimination();
    if (result.stalemate) sounds.playStalemate();

    if (result.gameOver) {
      sounds.playWin();
      const statusEl = document.getElementById("status") as HTMLElement;
      if (statusEl) {
        statusEl.textContent = `🏆 ${FACTION_COLORS[result.winner_faction!].name} hat gewonnen!`;
      }
      autoBattleActive = false;
      const autoBattleBtn = document.getElementById(
        "auto-battle-btn",
      ) as HTMLButtonElement;
      if (autoBattleBtn) {
        autoBattleBtn.textContent = "🤖 Auto Battle";
        autoBattleBtn.classList.remove("active");
      }

      // Learn from this game if there were book moves
      if (autoBattleBookMoves.length > 0) {
        const winnerFaction = result.winner_faction;
        learnFromGame(autoBattleBookMoves, winnerFaction ?? null);
        saveLearnedDataToStorage();
        console.log(
          `Opening book: Learned from auto-battle game (${autoBattleBookMoves.length} book moves, winner: ${winnerFaction || "draw"})`,
        );
        autoBattleBookMoves = [];
      }
    } else if (autoBattleActive) {
      triggerAutoMove();
    }
  }, 2200);
}

// ─── Promotion Overlay ──────────────────────────────────────────────

function showPromotion(piece: Piece): void {
  const color = FACTION_COLORS[piece.faction];
  const names = {
    queen: "Dame",
    rook: "Turm",
    bishop: "Läufer",
    knight: "Springer",
  };
  const symbols = { queen: "♛", rook: "♜", bishop: "♝", knight: "♞" };
  const keyHints = { queen: "Q", rook: "R", bishop: "B", knight: "N" };

  const settings = loadSettings();
  const autoQueen = settings.autoQueen === true;

  const promotionOverlay = document.getElementById(
    "promotion-overlay",
  ) as HTMLElement;
  promotionOverlay.innerHTML = `
    <div class="promotion-box">
      <div class="promotion-title" style="color:${color.primary}">
        Bauer promoviert! Wähle eine Figur:
      </div>
      <div class="promotion-preview" id="promotion-preview" style="color:${color.primary}; min-height: 80px; display: flex; align-items: center; justify-content: center; margin: 10px 0;">
        <span class="preview-symbol" style="font-size: 64px; opacity: 0.3; transition: all 0.15s ease;">${symbols.queen}</span>
        <span class="preview-name" style="font-size: 18px; margin-left: 12px; font-weight: bold; opacity: 0; transition: opacity 0.15s ease;"></span>
      </div>
      <div class="promotion-choices">
        ${(
          PROMOTION_CHOICES as readonly (
            "queen" | "rook" | "bishop" | "knight"
          )[]
        )
          .map(
            (type: "queen" | "rook" | "bishop" | "knight") => `
          <button class="promotion-choice" data-type="${type}" data-key="${keyHints[type]}" style="border-color:${color.primary}" title="${names[type]} (Taste: ${keyHints[type]})" aria-label="${names[type]} (Taste: ${keyHints[type]})">
            <span class="choice-symbol">${symbols[type]}</span>
            <span class="choice-name">${names[type]}</span>
            <span class="choice-key">${keyHints[type]}</span>
          </button>
        `,
          )
          .join("")}
      </div>
      <div class="promotion-options">
        <label class="auto-queen-label">
          <input type="checkbox" id="auto-queen-checkbox" ${autoQueen ? "checked" : ""}>
          <span>Immer automatisch zur Dame promovieren</span>
        </label>
      </div>
    </div>
  `;
  promotionOverlay.classList.add("visible");

  // Preview hover handlers
  const previewEl = document.getElementById("promotion-preview");
  const previewSymbol = previewEl?.querySelector(
    ".preview-symbol",
  ) as HTMLElement;
  const previewName = previewEl?.querySelector(".preview-name") as HTMLElement;

  if (previewEl && previewSymbol && previewName) {
    promotionOverlay.querySelectorAll(".promotion-choice").forEach((btn) => {
      btn.addEventListener("mouseenter", () => {
        const type = (btn as HTMLElement).dataset.type as
          "queen" | "rook" | "bishop" | "knight" | undefined;
        if (!type) return;
        previewSymbol.textContent = symbols[type];
        previewName.textContent = names[type];
        previewSymbol.style.opacity = "1";
        previewSymbol.style.transform = "scale(1.2)";
        previewName.style.opacity = "1";
      });
      btn.addEventListener("mouseleave", () => {
        previewSymbol.style.opacity = "0.3";
        previewSymbol.style.transform = "scale(1)";
        previewName.style.opacity = "0";
      });
    });
  }

  const autoQueenCheckbox = document.getElementById(
    "auto-queen-checkbox",
  ) as HTMLInputElement;
  if (autoQueenCheckbox) {
    autoQueenCheckbox.addEventListener("change", (e: Event) => {
      const settings = loadSettings();
      settings.autoQueen = (e.target as HTMLInputElement).checked;
      saveSettings(settings);
    });
  }

  if (autoQueen) {
    setTimeout(() => {
      const promotionOverlay = document.getElementById(
        "promotion-overlay",
      ) as HTMLElement;
      promotionOverlay.classList.remove("visible");
      const result = game.completePromotion("queen");
      if (result) {
        handlePromotionResult(result, piece);
      }
    }, 100);
    return;
  }

  promotionOverlay.querySelectorAll(".promotion-choice").forEach((btn) => {
    btn.addEventListener("click", () => {
      const newType = (btn as HTMLElement).dataset.type;
      if (!newType) return;
      promotionOverlay.classList.remove("visible");
      const result = game.completePromotion(
        newType as "queen" | "rook" | "bishop" | "knight",
      );
      if (result) {
        handlePromotionResult(result, piece);
      }
    });
  });

  const keyHandler = (e: KeyboardEvent) => {
    if (!promotionOverlay.classList.contains("visible")) return;

    const key = e.key.toLowerCase();
    const keyMap: Record<string, "queen" | "rook" | "bishop" | "knight"> = {
      q: "queen",
      r: "rook",
      b: "bishop",
      n: "knight",
    };

    if (keyMap[key]) {
      e.preventDefault();
      promotionOverlay.classList.remove("visible");
      const result = game.completePromotion(keyMap[key]);
      if (result) {
        handlePromotionResult(result, piece);
      }
      document.removeEventListener("keydown", keyHandler);
    }
  };

  document.addEventListener("keydown", keyHandler);

  const cleanup = () => {
    document.removeEventListener("keydown", keyHandler);
    promotionOverlay.removeEventListener("transitionend", cleanup);
  };
  promotionOverlay.addEventListener("transitionend", cleanup);
}

function handlePromotionResult(result: GameResult | null, piece: Piece): void {
  if (!result) return;
  addToLog(result);
  renderer.removePiece(piece.id);
  renderer.renderPiece(piece);
  updateUI();
  sounds.playPromotion();
}

// ─── Auto-Battle Button ────────────────────────────────────────────

function initEventListeners(): void {
  const autoBattleBtn = document.getElementById(
    "auto-battle-btn",
  ) as HTMLButtonElement;
  autoBattleBtn?.addEventListener("click", () => {
    if (game.state === "game_over") return;
    cancelSoloSearch();
    autoBattleActive = !autoBattleActive;
    if (autoBattleActive) {
      autoBattleBtn.textContent = "⏹ Auto Battle Stoppen";
      autoBattleBtn.classList.add("active");
      // NOTE: main-thread pondering (startPondering) intentionally omitted here
      // too — it runs a synchronous minimax (depth up to 12) in the calling
      // thread and would freeze the UI before the first auto-move is computed.
      // Auto-Battle uses calculateBestMoveWorker() (the AI Web Worker) instead.
      triggerAutoMove();
    } else {
      autoBattleBtn.textContent = "🤖 Auto Battle";
      autoBattleBtn.classList.remove("active");
      clearTimeout(autoBattleTimer!);
    }
    saveSettings({ ...loadSettings(), autoBattle: autoBattleActive });
  });

  const undoBtn = document.getElementById("undo-btn") as HTMLButtonElement;
  undoBtn?.addEventListener("click", () => {
    const snap = game.undo();
    if (snap) updateUI();
  });

  const rpsToggle = document.getElementById("rps-toggle") as HTMLInputElement;
  rpsToggle?.addEventListener("change", (e: Event) => {
    game.rpsEnabled = (e.target as HTMLInputElement).checked;
    updateUI();
    saveSettings({
      ...loadSettings(),
      rpsEnabled: (e.target as HTMLInputElement).checked,
    });
  });

  const soundToggle = document.getElementById(
    "sound-toggle",
  ) as HTMLInputElement;
  soundToggle?.addEventListener("change", (e: Event) => {
    sounds.toggle((e.target as HTMLInputElement).checked);
    saveSettings({
      ...loadSettings(),
      soundEnabled: (e.target as HTMLInputElement).checked,
    });
  });

  const animationsToggle = document.getElementById(
    "animations-toggle",
  ) as HTMLInputElement | null;
  animationsToggle?.addEventListener("change", (e: Event) => {
    const enabled = (e.target as HTMLInputElement).checked;
    document.documentElement.classList.toggle("animations-disabled", !enabled);
    saveSettings({ ...loadSettings(), animationsEnabled: enabled });
  });

  const depthSlider = document.getElementById(
    "depth-slider",
  ) as HTMLInputElement;
  const depthLabel = document.getElementById("depth-label") as HTMLElement;
  depthSlider?.addEventListener("input", (e: Event) => {
    const depth = parseInt((e.target as HTMLInputElement).value) as
      1 | 2 | 3 | 4;
    setAIDepth(depth);
    depthLabel.textContent = "KI: " + depthNames[depth];
    saveSettings({ ...loadSettings(), aiDepth: depth });
  });

  let currentBoardRotation = 0;
  const rotateBtn = document.getElementById("rotate-btn") as HTMLButtonElement;
  rotateBtn?.addEventListener("click", () => {
    currentBoardRotation += 120;
    renderer.setRotation(currentBoardRotation);
    saveSettings({ ...loadSettings(), boardRotation: currentBoardRotation });
  });

  const restartBtn = document.getElementById(
    "restart-btn",
  ) as HTMLButtonElement;
  restartBtn?.addEventListener("click", () => {
    cancelSoloSearch();
    const combatOverlay = document.getElementById(
      "combat-overlay",
    ) as HTMLElement;
    const promotionOverlay = document.getElementById(
      "promotion-overlay",
    ) as HTMLElement;
    combatOverlay.classList.remove("visible");
    promotionOverlay.classList.remove("visible");
    const boardGroup = document.getElementById("board-group");
    boardGroup?.querySelectorAll(".piece").forEach((el) => el.remove());
    renderer.pieceElements.clear();
    game.init(renderer.cells);
    game.clearUndoStack();
    autoBattleBookMoves = []; // Clear learning tracking
    const moveLogEl = document.getElementById("move-log") as HTMLElement;
    moveLogEl.innerHTML = "";
    for (const p of game.getAlivePieces()) renderer.renderPiece(p);
    for (const fac of [FACTION.FIRE, FACTION.WATER, FACTION.NATURE]) {
      const el = document.getElementById(`panel-${fac}`);
      if (el) el.classList.remove("eliminated");
    }

    autoBattleActive = false;
    const autoBattleBtn = document.getElementById(
      "auto-battle-btn",
    ) as HTMLButtonElement;
    if (autoBattleBtn) {
      autoBattleBtn.textContent = "🤖 Auto Battle";
      autoBattleBtn.classList.remove("active");
    }
    clearTimeout(autoBattleTimer!);

    saveSettings({ ...loadSettings(), autoBattle: false });
    updateUI();
  });

  const playAs = document.getElementById("play-as") as HTMLSelectElement | null;
  playAs?.addEventListener("change", () => {
    cancelSoloSearch();
    humanFaction = parseHumanFaction(playAs.value);
    if (humanFaction && autoBattleActive) {
      autoBattleActive = false;
      clearTimeout(autoBattleTimer!);
      const autoBtn = document.getElementById(
        "auto-battle-btn",
      ) as HTMLButtonElement | null;
      if (autoBtn) {
        autoBtn.textContent = "🤖 Auto Battle";
        autoBtn.classList.remove("active");
      }
    }
    saveSettings({
      ...loadSettings(),
      humanFaction,
      autoBattle: autoBattleActive,
    });
    updateUI();
  });

  // Overlay close buttons hide themselves and do not call updateUI. A click
  // that bubbles here resumes an engine turn once the overlay is gone.
  document.addEventListener("click", () => {
    maybePlaySoloMove();
  });

  const saveBtn = document.getElementById("save-btn") as HTMLButtonElement;
  saveBtn?.addEventListener("click", async () => {
    const { downloadGame } = await loadReplayModule();
    downloadGame(
      game,
      `trischach-${new Date().toISOString().slice(0, 10)}.tspn`,
    );
  });

  const copyBtn = document.getElementById("copy-btn") as HTMLButtonElement;
  copyBtn?.addEventListener("click", async () => {
    try {
      const { copyGameToClipboard } = await loadReplayModule();
      await copyGameToClipboard(game);
      copyBtn.textContent = "✅ Kopiert!";
      setTimeout(() => {
        copyBtn.textContent = "📋 Kopieren";
      }, 1500);
    } catch (e) {
      console.error("Copy failed:", e);
      copyBtn.textContent = "❌ Fehler";
      setTimeout(() => {
        copyBtn.textContent = "📋 Kopieren";
      }, 1500);
    }
  });

  const puzzleBtn = document.getElementById("puzzle-btn") as HTMLButtonElement;
  puzzleBtn?.addEventListener("click", () => {
    if (game.state === "game_over") return;
    showPuzzleMenu();
  });

  const statsBtn = document.getElementById("stats-btn") as HTMLButtonElement;
  statsBtn?.addEventListener("click", () => {
    showStatsDashboard();
  });

  const loadBtn = document.getElementById("load-btn") as HTMLButtonElement;
  const fileInput = document.createElement("input");
  fileInput.type = "file";
  fileInput.accept = ".tspn,text/plain";
  fileInput.style.display = "none";
  document.body.appendChild(fileInput);

  loadBtn?.addEventListener("click", () => {
    fileInput.click();
  });

  fileInput.addEventListener("change", async (e: Event) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;

    try {
      const { loadGameFromFile, reconstructGameFromTSPN } =
        await loadReplayModule();
      const parsed = await loadGameFromFile(file);
      console.log("Loaded game:", parsed.headers);

      const GameCtor = await loadGame();
      const { game: replayGame, controller: replayController } =
        reconstructGameFromTSPN(parsed, GameCtor, renderer.cells);

      autoBattleActive = false;
      const autoBattleBtn = document.getElementById(
        "auto-battle-btn",
      ) as HTMLButtonElement;
      if (autoBattleBtn) {
        autoBattleBtn.textContent = "🤖 Auto Battle";
        autoBattleBtn.classList.remove("active");
      }
      clearTimeout(autoBattleTimer!);

      Object.assign(game, replayGame);
      game.clearUndoStack();

      window.replayController = replayController;

      const boardGroup = document.getElementById("board-group");
      boardGroup?.querySelectorAll(".piece").forEach((el) => el.remove());
      renderer.pieceElements.clear();
      for (const p of game.getAlivePieces()) renderer.renderPiece(p);

      const moveLogEl = document.getElementById("move-log") as HTMLElement;
      moveLogEl.innerHTML = "";
      for (const move of parsed.moves) {
        const entry = document.createElement("div");
        entry.className = `move-entry ${move.faction}`;
        entry.textContent = `${move.faction} ${move.san}`;
        moveLogEl.appendChild(entry);
      }

      showReplayControls();
      updateReplayUI();
      updateUI();
    } catch (err) {
      console.error("Load failed:", err);
      alert("Fehler beim Laden: " + (err as Error).message);
    }

    fileInput.value = "";
  });

  // Replay controls
  let replayPlayTimer: ReturnType<typeof setInterval> | null = null;

  /**
   * Jump into an in-game replay at the given move index (-1 = start position).
   * Serializes the current game to TSPN, rebuilds it through the replay system
   * and steps to the chosen position — same path as loading a .tspn file.
   */
  async function jumpToMove(moveIndex: number): Promise<void> {
    if (game.moveHistory.length === 0) return;
    try {
      const { serializeGame, parseTSPN, reconstructGameFromTSPN } =
        await loadReplayModule();
      const tspn = serializeGame(game);
      const parsed = parseTSPN(tspn);
      if (parsed.moves.length === 0) return;

      const GameCtor = await loadGame();
      const { game: replayGame, controller: replayController } =
        reconstructGameFromTSPN(parsed, GameCtor, renderer.cells);

      autoBattleActive = false;
      const autoBattleBtn = document.getElementById(
        "auto-battle-btn",
      ) as HTMLButtonElement;
      if (autoBattleBtn) {
        autoBattleBtn.textContent = "🤖 Auto Battle";
        autoBattleBtn.classList.remove("active");
      }
      clearTimeout(autoBattleTimer!);

      const clamped = Math.min(
        Math.max(moveIndex, -1),
        parsed.moves.length - 1,
      );
      Object.assign(game, replayGame);
      game.clearUndoStack();
      window.replayController = replayController;
      replayController.goTo(clamped);
      const state = replayController.getCurrentState();
      if (state) applyGameState(state);
      else updateUI();

      showReplayControls();
      updateReplayUI();
    } catch (err) {
      console.error("Jump to move failed:", err);
    }
  }
  jumpToMoveImpl = jumpToMove;

  function showReplayControls(): void {
    const replayControls = document.getElementById("replay-controls");
    if (replayControls) replayControls.style.display = "flex";
  }

  function hideReplayControls(): void {
    const replayControls = document.getElementById("replay-controls");
    if (replayControls) replayControls.style.display = "none";
    clearReplayAnalysis();
  }

  function clearReplayAnalysis(): void {
    const panel = document.getElementById("replay-analysis");
    const text = document.getElementById("replay-analysis-text");
    if (panel) panel.hidden = true;
    if (text) text.textContent = "–";
  }

  function renderReplayAnalysis(): void {
    const panel = document.getElementById("replay-analysis");
    const text = document.getElementById("replay-analysis-text");
    if (!panel || !text) return;

    text.textContent = "Analysiere…";
    panel.hidden = false;

    // Defer so the "Analysiere…" paint can show before a blocking search.
    requestAnimationFrame(async () => {
      try {
        const { analyzePosition, renderAnalysisToHTML } =
          await loadAnalysisModule();
        const depth = Math.min(3, getAIDepth());
        const result = analyzePosition(game, depth);
        text.innerHTML = renderAnalysisToHTML(result);
      } catch (err) {
        console.error("Analysis failed:", err);
        text.textContent = "Analyse fehlgeschlagen";
      }
    });
  }

  function updateReplayUI(): void {
    const controller = window.replayController;
    if (!controller) return;

    const moveInfo = document.getElementById("replay-move-info") as HTMLElement;
    if (moveInfo) {
      const moveNum = controller.getCurrentMoveNumber();
      const total = controller.getTotalMoves();
      moveInfo.textContent = `Zug ${moveNum} / ${total}`;
    }

    const isAtEnd = !controller.canGoForward();
    const isAtStart = !controller.canGoBack();

    const replayFirst = document.getElementById(
      "replay-first",
    ) as HTMLButtonElement;
    const replayPrev = document.getElementById(
      "replay-prev",
    ) as HTMLButtonElement;
    const replayNext = document.getElementById(
      "replay-next",
    ) as HTMLButtonElement;
    const replayLast = document.getElementById(
      "replay-last",
    ) as HTMLButtonElement;
    const replayPlay = document.getElementById(
      "replay-play",
    ) as HTMLButtonElement;
    const replayPause = document.getElementById(
      "replay-pause",
    ) as HTMLButtonElement;

    if (replayFirst) replayFirst.disabled = isAtStart;
    if (replayPrev) replayPrev.disabled = isAtStart;
    if (replayNext) replayNext.disabled = isAtEnd;
    if (replayLast) replayLast.disabled = isAtEnd;
    if (replayPlay)
      replayPlay.style.display = isAtEnd ? "none" : "inline-block";
    if (replayPause) replayPause.style.display = "none";

    const moveEntries = document.querySelectorAll(".move-entry");
    moveEntries.forEach((entry, index) => {
      entry.classList.toggle(
        "current-move",
        index === controller.getCurrentMoveNumber() - 1,
      );
    });

    // Clear stale analysis when the position changes (user re-runs manually).
    clearReplayAnalysis();
  }

  function replayStep(delta: number): void {
    const controller = window.replayController;
    if (!controller) return;

    if (delta > 0) controller.next();
    else controller.previous();

    const state = controller.getCurrentState();
    if (state) applyGameState(state);

    updateReplayUI();
  }

  function replayPlay(): void {
    stopReplayPlay();
    const controller = window.replayController;
    if (!controller || !controller.canGoForward()) return;

    const replayPlay = document.getElementById(
      "replay-play",
    ) as HTMLButtonElement;
    const replayPause = document.getElementById(
      "replay-pause",
    ) as HTMLButtonElement;
    if (replayPlay) replayPlay.style.display = "none";
    if (replayPause) replayPause.style.display = "inline-block";

    const speed = parseFloat(
      (document.getElementById("replay-speed") as HTMLInputElement)?.value ||
        "1",
    );
    const delay = 1000 / speed;

    replayPlayTimer = setInterval(() => {
      const c = window.replayController;
      if (!c || !c.canGoForward()) {
        stopReplayPlay();
        return;
      }
      c.next();
      const state = c.getCurrentState();
      if (state) applyGameState(state);
      updateReplayUI();
    }, delay);
  }

  function stopReplayPlay(): void {
    if (replayPlayTimer) {
      clearInterval(replayPlayTimer);
      replayPlayTimer = null;
    }
    const replayPlay = document.getElementById(
      "replay-play",
    ) as HTMLButtonElement;
    const replayPause = document.getElementById(
      "replay-pause",
    ) as HTMLButtonElement;
    if (replayPlay) replayPlay.style.display = "inline-block";
    if (replayPause) replayPause.style.display = "none";
  }

  function applyGameState(state: ReplayStateSnapshot): void {
    const boardGroup = document.getElementById("board-group");
    if (boardGroup) {
      boardGroup.querySelectorAll(".piece").forEach((el) => el.remove());
    }
    renderer.pieceElements.clear();

    for (const p of state.pieces) {
      if (p.alive) {
        const piece = game.pieces.find((pc: Piece) => pc.id === p.id);
        if (piece) {
          // state.pieces carries plain {q,r} snapshots — restore a real Hex
          // so piece.pos.add()/key keep working after Object.assign.
          const pos = p.pos instanceof Hex ? p.pos : new Hex(p.pos.q, p.pos.r);
          Object.assign(piece, p, { pos });
          renderer.renderPiece(piece);
        }
      }
    }

    game.currentFaction = state.currentFaction as Faction;
    game.currentFactionIdx = state.currentFactionIdx;
    game.state = state.state as GameState;
    game.eliminatedFactions = new Set(state.eliminatedFactions as Faction[]);
    game.capturedPieces = {
      fire: (state.capturedPieces.fire || [])
        .map((id: string) => game.pieces.find((p: Piece) => p.id === id))
        .filter(Boolean),
      water: (state.capturedPieces.water || [])
        .map((id: string) => game.pieces.find((p: Piece) => p.id === id))
        .filter(Boolean),
      nature: (state.capturedPieces.nature || [])
        .map((id: string) => game.pieces.find((p: Piece) => p.id === id))
        .filter(Boolean),
    } as Record<Faction, Piece[]>;

    for (const fac of [FACTION.FIRE, FACTION.WATER, FACTION.NATURE]) {
      const el = document.getElementById(`panel-${fac}`);
      if (el && state.eliminatedFactions?.includes(fac)) {
        el.classList.add("eliminated");
      } else if (el) {
        el.classList.remove("eliminated");
      }
    }

    updateUI();
  }

  // Replay Controls Event Listeners
  const replayFirst = document.getElementById(
    "replay-first",
  ) as HTMLButtonElement;
  const replayPrev = document.getElementById(
    "replay-prev",
  ) as HTMLButtonElement;
  const replayPlayBtn = document.getElementById(
    "replay-play",
  ) as HTMLButtonElement;
  const replayPauseBtn = document.getElementById(
    "replay-pause",
  ) as HTMLButtonElement;
  const replayNext = document.getElementById(
    "replay-next",
  ) as HTMLButtonElement;
  const replayLast = document.getElementById(
    "replay-last",
  ) as HTMLButtonElement;
  const replaySpeed = document.getElementById(
    "replay-speed",
  ) as HTMLInputElement;

  replayFirst?.addEventListener("click", () => {
    const controller = window.replayController as ReplayController;
    controller.goToStart();
    applyGameState(controller.getCurrentState());
    updateReplayUI();
  });

  replayPrev?.addEventListener("click", () => {
    const controller = window.replayController as ReplayController;
    controller.previous();
    applyGameState(controller.getCurrentState());
    updateReplayUI();
  });

  replayPlayBtn?.addEventListener("click", () => {
    replayPlay();
  });

  replayPauseBtn?.addEventListener("click", () => {
    stopReplayPlay();
  });

  replayNext?.addEventListener("click", () => {
    const controller = window.replayController as ReplayController;
    controller.next();
    applyGameState(controller.getCurrentState());
    updateReplayUI();
  });

  replayLast?.addEventListener("click", () => {
    const controller = window.replayController as ReplayController;
    controller.goToEnd();
    applyGameState(controller.getCurrentState());
    updateReplayUI();
  });

  const replayExport = document.getElementById(
    "replay-export",
  ) as HTMLButtonElement;
  replayExport?.addEventListener("click", () => {
    const controller = window.replayController;
    if (!controller) return;

    try {
      const tspn = controller.exportTSPN();
      const blob = new Blob([tspn], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const moveNum = controller.getCurrentMoveNumber();
      const total = controller.getTotalMoves();
      a.download = `trischach-pos-${moveNum}-of-${total}-${new Date().toISOString().slice(0, 10)}.tspn`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
      alert("Export fehlgeschlagen: " + (err as Error).message);
    }
  });

  const replayExportFull = document.getElementById(
    "replay-export-full",
  ) as HTMLButtonElement;
  replayExportFull?.addEventListener("click", () => {
    const controller = window.replayController;
    if (!controller) return;

    try {
      // Export the full game from initial position to end
      const tspn = controller.exportTSPNFull();
      const blob = new Blob([tspn], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const total = controller.getTotalMoves();
      a.download = `trischach-full-${total}moves-${new Date().toISOString().slice(0, 10)}.tspn`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Full export failed:", err);
      alert("Export fehlgeschlagen: " + (err as Error).message);
    }
  });

  replaySpeed?.addEventListener("input", () => {
    if (replayPlayTimer) {
      replayPlay();
    }
  });

  const replayAnalyze = document.getElementById(
    "replay-analyze",
  ) as HTMLButtonElement | null;
  replayAnalyze?.addEventListener("click", () => {
    if (!window.replayController) {
      showHintToast("Zuerst eine Partie laden");
      return;
    }
    renderReplayAnalysis();
  });

  // ─── Puzzle Mode ────────────────────────────────────────────────────

  let puzzleGame: Game | null = null;
  let puzzleRenderer: BoardRenderer | null = null;
  let puzzleBoardGroup: SVGGElement | null = null;
  let puzzleTimerInterval: ReturnType<typeof setInterval> | null = null;
  let puzzleStartTime: number = 0;

  function puzzleStatsHtml(): string {
    const s = puzzleApi().getPuzzleSessionStats();
    if (s.attempts === 0) return "";
    const rate = Math.round((s.solved / s.attempts) * 100);
    const avg = s.solved > 0 ? Math.round(s.totalSeconds / s.solved) : null;
    const fmt = (sec: number | null) =>
      sec === null
        ? "–"
        : `${Math.floor(sec / 60)}:${(sec % 60).toString().padStart(2, "0")}`;
    return `
        <div class="puzzle-streak" id="puzzle-session-stats">
          <span>✅ Gelöst: <strong>${s.solved}</strong>/${s.attempts} (${rate}%)</span>
          <span>⏱ Ø: <strong>${fmt(avg)}</strong></span>
          <span>🏅 Best: <strong>${fmt(s.bestTimeSeconds)}</strong></span>
        </div>`;
  }

  /** CSS class for the current day's daily-puzzle difficulty tier. */
  function dailyDifficultyClass(): string {
    return puzzleApi().preferredDifficulty(puzzleApi().todayISO());
  }

  /** Human-readable label for the current day's daily-puzzle difficulty. */
  function dailyDifficultyLabel(): string {
    const tier = puzzleApi().preferredDifficulty(puzzleApi().todayISO());
    return tier === "easy" ? "Leicht" : tier === "medium" ? "Medium" : "Schwer";
  }

  async function showPuzzleMenu(): Promise<void> {
    // Puzzle-Statistiken & -Logik kommen aus dem Lazy-Chunk.
    await loadPuzzleModule();
    const streak = puzzleApi().getPuzzleStreak();
    const dailyDone = puzzleApi().isDailySolvedToday();
    puzzleOverlay.innerHTML = `
      <div class="puzzle-box">
        <div class="puzzle-header">
          <h2 class="puzzle-title">♟ Puzzle Modus</h2>
          <button class="puzzle-close-btn" id="puzzle-close-btn" title="Schließen" aria-label="Schließen">✕</button>
        </div>
        <div class="puzzle-info" id="puzzle-menu-info">
          <div class="puzzle-info-item"><strong>Tagespuzzle</strong></div>
          <span class="puzzle-difficulty ${dailyDone ? "medium" : dailyDifficultyClass()}" id="puzzle-daily-difficulty">${dailyDone ? "GELÖST" : dailyDifficultyLabel()}</span>
        </div>
        <div class="puzzle-streak">
          <span>🔥 Streak: <strong>${streak.current}</strong></span>
          <span class="streak-best">Best: <strong>${streak.best}</strong></span>
          <span>Gesamt: <strong>${streak.totalDailySolved}</strong></span>
        </div>
        ${puzzleStatsHtml()}
        <div class="puzzle-actions">
          <button class="puzzle-btn primary" id="puzzle-daily-btn">${dailyDone ? "📅 Tagespuzzle (nochmal)" : "📅 Tagespuzzle"}</button>
          <button class="puzzle-btn secondary" id="puzzle-generate-btn">🔄 Neu generieren</button>
          <button class="puzzle-btn secondary" id="puzzle-rps-btn">🎯 RPS-Taktik</button>
          <button class="puzzle-btn secondary" id="puzzle-continue-btn" style="display: none">▶️ Fortsetzen</button>
        </div>
      </div>
    `;
    puzzleOverlay.classList.add("visible");

    document
      .getElementById("puzzle-close-btn")
      ?.addEventListener("click", () => {
        puzzleOverlay.classList.remove("visible");
      });

    document
      .getElementById("puzzle-daily-btn")
      ?.addEventListener("click", async () => {
        await loadAndShowDailyPuzzle();
      });

    document
      .getElementById("puzzle-generate-btn")
      ?.addEventListener("click", async () => {
        await generateAndShowPuzzles();
      });

    document.getElementById("puzzle-rps-btn")?.addEventListener("click", () => {
      showRpsTacticPuzzle();
    });

    document
      .getElementById("puzzle-continue-btn")
      ?.addEventListener("click", async () => {
        const state = puzzleApi().getPuzzleState();
        if (state.currentPuzzle) {
          await showPuzzleBoard(state.currentPuzzle, { resume: true });
        }
      });

    // Check for saved progress
    const state = puzzleApi().getPuzzleState();
    if (state.currentPuzzle) {
      (
        document.getElementById("puzzle-continue-btn") as HTMLButtonElement
      ).style.display = "inline-block";
    }
  }

  async function loadAndShowDailyPuzzle(): Promise<void> {
    showLoading("Lade Tagespuzzle...");
    const daily = await puzzleApi().getDailyPuzzle();
    hideLoading();
    if (daily) {
      await showPuzzleBoard(daily);
    } else {
      showError("Konnte Tagespuzzle nicht laden.");
    }
  }

  async function generateAndShowPuzzles(): Promise<void> {
    showLoading("Lade Puzzles...");
    const puzzles = await puzzleApi().loadShippedPuzzles();
    hideLoading();
    // Show a random sample of up to 10 from the shipped pool.
    const sample = [...puzzles].sort(() => Math.random() - 0.5).slice(0, 10);
    if (sample.length > 0) {
      showPuzzleSelection(sample);
    } else {
      showError("Keine Puzzles gefunden.");
    }
  }

  function showPuzzleSelection(puzzles: Puzzle[]): void {
    puzzleOverlay.innerHTML = `
      <div class="puzzle-box">
        <div class="puzzle-header">
          <h2 class="puzzle-title">Wähle ein Puzzle</h2>
          <button class="puzzle-close-btn" id="puzzle-back-btn" title="Zurück" aria-label="Zurück">✕</button>
        </div>
        <div class="puzzle-solution-list" style="max-height: 400px;">
          ${puzzles
            .map(
              (p, i) => `
            <div class="puzzle-move" data-index="${i}">
              <span class="puzzle-move-number">${i + 1}.</span>
              <span class="puzzle-move-san">Matt in ${p.mateIn} (${escapeHtml(p.difficulty)})</span>
              <span class="puzzle-move-status pending">${p.faction}</span>
            </div>
          `,
            )
            .join("")}
        </div>
      </div>
    `;

    document
      .getElementById("puzzle-back-btn")
      ?.addEventListener("click", () => {
        showPuzzleMenu();
      });

    puzzleOverlay.querySelectorAll(".puzzle-move[data-index]").forEach((el) => {
      el.addEventListener("click", async () => {
        const index = parseInt(el.getAttribute("data-index")!);
        const puzzle = puzzles[index];
        if (puzzle) await showPuzzleBoard(puzzle!);
      });
    });
  }

  async function showRpsTacticPuzzle(): Promise<void> {
    const { generateRpsPuzzles, deserializeRpsPosition, evaluateRpsMove } =
      rpsPuzzleApi();
    const puzzles = generateRpsPuzzles(1);
    const puzzle = puzzles[0];
    if (!puzzle) {
      showError("Keine RPS-Taktik-Puzzles verfügbar.");
      return;
    }
    const game = deserializeRpsPosition(puzzle.fen);
    if (!game) {
      showError("Ungültige Puzzle-Position.");
      return;
    }

    puzzleOverlay.innerHTML = `
      <div class="puzzle-box">
        <div class="puzzle-header">
          <h2 class="puzzle-title">🎯 RPS-Taktik</h2>
          <button class="puzzle-close-btn" id="rps-close-btn" title="Abbrechen" aria-label="Abbrechen">✕</button>
        </div>
        <div class="puzzle-info">
          <span class="puzzle-difficulty ${escapeHtml(puzzle.difficulty)}">${escapeHtml(puzzle.difficulty.toUpperCase())}</span>
          <span class="puzzle-info-item">Am Zug: <strong>${FACTION_COLORS[puzzle.sideToMove].name}</strong></span>
        </div>
        <div id="rps-board-wrapper" class="puzzle-board-container"></div>
        <div class="puzzle-info" id="rps-feedback">Wähle eine Figur und schlage die richtige gegnerische Figur (RPS-Vorteil!).</div>
        <div class="puzzle-actions">
          <button class="puzzle-btn secondary" id="rps-new-btn">🔄 Neues Puzzle</button>
          <button class="puzzle-btn secondary" id="rps-menu-btn">📋 Menü</button>
        </div>
      </div>
    `;
    puzzleOverlay.classList.add("visible");

    const boardWrapper = document.getElementById("rps-board-wrapper")!;
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.id = "rps-board-svg";
    boardWrapper.appendChild(svg);

    const rpsRenderer = new BoardRenderer(svg);
    rpsRenderer.render();
    for (const p of game.getAlivePieces()) rpsRenderer.renderPiece(p);

    let selectedPieceKey: string | null = null;

    rpsRenderer.onCellClick = (hex: { q: number; r: number }) => {
      const cell = rpsRenderer.cells.get(`${hex.q},${hex.r}`);
      if (!cell) return;
      const piece = game.getPieceAt(cell.hex);
      if (piece && piece.faction === puzzle.sideToMove) {
        selectedPieceKey = piece.pos.key;
        rpsRenderer.clearSelection();
        rpsRenderer.selectCell(piece.pos);
        rpsRenderer.clearHighlights();
        rpsRenderer.highlightCells(
          game
            .getAlivePieces()
            .filter((t) => t.alive && t.faction !== puzzle.sideToMove)
            .map((t) => t.pos),
          "highlight-attack",
        );
        return;
      }
      if (selectedPieceKey && piece && piece.faction !== puzzle.sideToMove) {
        const result = evaluateRpsMove(puzzle, selectedPieceKey, piece.pos.key);
        const fb = document.getElementById("rps-feedback")!;
        if (result.correct) {
          fb.innerHTML = `✅ <strong>Richtig!</strong> ${escapeHtml(result.rationale)}`;
          rpsRenderer.clearHighlights();
        } else {
          fb.innerHTML = `❌ <strong>Falsch.</strong> ${escapeHtml(result.rationale)}`;
        }
        selectedPieceKey = null;
      }
    };

    document.getElementById("rps-close-btn")?.addEventListener("click", () => {
      puzzleOverlay.classList.remove("visible");
    });
    document.getElementById("rps-new-btn")?.addEventListener("click", () => {
      showRpsTacticPuzzle();
    });
    document.getElementById("rps-menu-btn")?.addEventListener("click", () => {
      showPuzzleMenu();
    });
  }

  async function showPuzzleBoard(
    puzzle: Puzzle,
    options: { resume?: boolean } = {},
  ): Promise<void> {
    // Create a new game from the puzzle position
    const testGame = await createGame();
    const cells = generateBoard();
    testGame.init(cells);

    // Deserialize the puzzle position
    const gameFromFen = await deserializePuzzleFEN(puzzle.fen);
    if (!gameFromFen) {
      showError("Ungültige Puzzle-Position.");
      return;
    }

    // Copy state to testGame
    Object.assign(testGame, gameFromFen);
    testGame.clearUndoStack(); // No undo in puzzle mode

    // Fresh open resets progress; "Fortsetzen" keeps in-memory puzzle state.
    if (!options.resume) {
      puzzleApi().loadPuzzle(puzzle);
    }
    puzzleGame = testGame;
    const state = puzzleApi().getPuzzleState();
    puzzleStartTime = state.startTime || Date.now();

    // Create a temporary renderer for the puzzle board
    const puzzleBoardContainer = document.createElement("div");
    puzzleBoardContainer.className = "puzzle-board-container";

    puzzleOverlay.innerHTML = `
      <div class="puzzle-box">
        <div class="puzzle-header">
          <h2 class="puzzle-title">♟ Matt in ${puzzle.mateIn}</h2>
          <button class="puzzle-close-btn" id="puzzle-close-btn" title="Abbrechen" aria-label="Abbrechen">✕</button>
        </div>
        <div class="puzzle-info">
          <span class="puzzle-difficulty ${escapeHtml(puzzle.difficulty)}">${escapeHtml(puzzle.difficulty.toUpperCase())}</span>
          <span class="puzzle-info-item">Am Zug: <strong>${FACTION_COLORS[puzzle.faction].name}</strong></span>
        </div>
        <div id="puzzle-board-wrapper" class="puzzle-board-container"></div>
        <div class="puzzle-progress">
          <div class="puzzle-progress-bar" id="puzzle-progress-bar" style="width: 0%"></div>
        </div>
        <div class="puzzle-timer" id="puzzle-timer">00:00</div>
        <div class="puzzle-solution-title">Lösungsweg</div>
        <div class="puzzle-solution-list" id="puzzle-solution-list"></div>
        <div class="puzzle-actions">
          <button class="puzzle-btn secondary" id="puzzle-hint-btn">💡 Hinweis</button>
          <button class="puzzle-btn danger" id="puzzle-giveup-btn">🏳 Aufgeben</button>
          <button class="puzzle-btn primary" id="puzzle-reset-btn" style="display: none">🔄 Neustart</button>
        </div>
      </div>
    `;
    puzzleOverlay.classList.add("visible");

    // Render the board in the wrapper
    const boardWrapper = document.getElementById("puzzle-board-wrapper")!;
    const puzzleSvg = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "svg",
    );
    puzzleSvg.id = "puzzle-board-svg";
    puzzleSvg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    boardWrapper.appendChild(puzzleSvg);

    puzzleRenderer = new BoardRenderer(puzzleSvg);
    puzzleRenderer.render();
    puzzleBoardGroup = puzzleRenderer.pieceElements.size
      ? puzzleSvg.querySelector("#board-group")
      : null;

    // Render pieces
    for (const p of puzzleGame.getAlivePieces()) {
      puzzleRenderer.renderPiece(p);
    }

    // Render solution list
    renderSolutionList(puzzle);

    // Start timer
    puzzleTimerInterval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - puzzleStartTime) / 1000);
      const mins = Math.floor(elapsed / 60)
        .toString()
        .padStart(2, "0");
      const secs = (elapsed % 60).toString().padStart(2, "0");
      (document.getElementById("puzzle-timer") as HTMLElement).textContent =
        `${mins}:${secs}`;
    }, 1000);

    // Event listeners
    document
      .getElementById("puzzle-close-btn")
      ?.addEventListener("click", () => {
        puzzleApi().abandonPuzzle();
        cleanupPuzzle();
        showPuzzleMenu();
      });

    document
      .getElementById("puzzle-hint-btn")
      ?.addEventListener("click", () => {
        const hint = puzzleApi().requestHint();
        if (hint) {
          showHintToast(`Zug: ${hint.san}`);
        }
      });

    document
      .getElementById("puzzle-giveup-btn")
      ?.addEventListener("click", () => {
        showPuzzleResult(false, puzzle);
      });

    document
      .getElementById("puzzle-reset-btn")
      ?.addEventListener("click", async () => {
        puzzleApi().resetPuzzle();
        await showPuzzleBoard(puzzle);
      });

    // Handle clicks on the puzzle board
    puzzleSvg.addEventListener("click", (e) =>
      handlePuzzleBoardClick(e, puzzleGame!),
    );
  }

  function renderSolutionList(puzzle: Puzzle): void {
    const listEl = document.getElementById("puzzle-solution-list")!;
    const state = puzzleApi().getPuzzleState();

    listEl.innerHTML = puzzle.solution
      .map((move, i) => {
        let statusClass = "pending";
        let statusText = "⏳";
        if (i < state.currentMoveIndex) {
          statusClass = "correct";
          statusText = "✅";
        } else if (i === state.currentMoveIndex) {
          statusClass = "current";
          statusText = "▶️";
        }
        return `
        <div class="puzzle-move ${statusClass}">
          <span class="puzzle-move-number">${i + 1}.</span>
          <span class="puzzle-move-san">${escapeHtml(move.san)}</span>
          <span class="puzzle-move-status ${statusClass}">${statusText}</span>
        </div>
      `;
      })
      .join("");

    // Update progress bar
    const progress = (state.currentMoveIndex / puzzle.solution.length) * 100;
    (
      document.getElementById("puzzle-progress-bar") as HTMLElement
    ).style.width = `${progress}%`;
  }

  function handlePuzzleBoardClick(event: MouseEvent, pg: Game): void {
    if (!pg || puzzleApi().getPuzzleState().isComplete) return;

    // Find clicked hex from SVG coordinates
    const svg = event.target as SVGElement;
    const boardGroup = svg.closest("#board-group") as SVGGElement;
    if (!boardGroup) return;

    // Get click coordinates relative to SVG
    const svgEl = document.getElementById(
      "puzzle-board-svg",
    ) as unknown as SVGSVGElement;
    const rect = svgEl.getBoundingClientRect();
    const clientX = event.clientX - rect.left;
    const clientY = event.clientY - rect.top;

    // Find hex under click (simplified - use renderer's hit testing)
    // For now, we'll use a simpler approach: check which piece was clicked
    const target = event.target as SVGGElement;
    const pieceEl = target.closest(".piece") as SVGGElement;
    const cellEl = target.closest(".cell") as SVGGElement;

    if (pieceEl) {
      const pieceId = pieceEl.getAttribute("data-piece-id");
      if (!pieceId) return;

      const piece = pg.getAlivePieces().find((p) => p.id === pieceId);
      if (!piece) return;

      const state = puzzleApi().getPuzzleState();
      if (state.currentMoveIndex >= state.currentPuzzle!.solution.length)
        return;

      const expectedMove =
        state.currentPuzzle!.solution[state.currentMoveIndex]!;

      if (pieceId === expectedMove.pieceId) {
        // Correct piece selected, now wait for target click
        pg.handleCellClick(piece.pos); // Select the piece
        showSelectionHighlights(piece);
      }
    } else if (cellEl) {
      const hexKey = cellEl.getAttribute("data-hex-key");
      if (!hexKey) return;

      const parts = hexKey.split(",");
      const q = Number(parts[0]);
      const r = Number(parts[1]);
      const targetHex = new Hex(q, r);

      // Try to make the move
      const expectedMove =
        puzzleApi().getPuzzleState().currentPuzzle!.solution[
          puzzleApi().getPuzzleState().currentMoveIndex
        ]!;
      const result = puzzleApi().makePuzzleMove(
        pg,
        expectedMove.pieceId,
        targetHex,
      );

      if (result.correct) {
        // Move was correct
        sounds.playMove();
        const move = pg.moveHistory[pg.moveHistory.length - 1]!;
        addToLog(move);
        puzzleGame
          ?.getAlivePieces()
          .forEach((p) => puzzleRenderer?.renderPiece(p));
        renderSolutionList(puzzleApi().getPuzzleState().currentPuzzle!);

        if (result.gameOver) {
          setTimeout(
            () =>
              showPuzzleResult(
                true,
                puzzleApi().getPuzzleState().currentPuzzle!,
              ),
            500,
          );
        }
      } else {
        // Wrong move
        sounds.playError();
        showPuzzleResult(false, puzzleApi().getPuzzleState().currentPuzzle!);
      }
    }

    pg._rebuildOccupiedMap();
  }

  function showSelectionHighlights(piece: Piece): void {
    if (!puzzleGame || !puzzleRenderer) return;
    puzzleRenderer.clearHighlights();
    puzzleRenderer.clearSelection();
    puzzleRenderer.selectCell(piece.pos);
    const { moves, attacks } = puzzleGame.getLegalMoves(piece);
    puzzleRenderer.highlightCells(moves, "highlight-move");
    if (puzzleGame.rpsEnabled) {
      // We don't have rpsAttacks here, just use attacks
      puzzleRenderer.highlightCells(attacks, "highlight-attack");
    } else {
      puzzleRenderer.highlightCells(attacks, "highlight-attack");
    }
  }

  function showPuzzleResult(success: boolean, puzzle: Puzzle): void {
    if (puzzleTimerInterval) {
      clearInterval(puzzleTimerInterval);
      puzzleTimerInterval = null;
    }

    const state = puzzleApi().getPuzzleState();
    const elapsed = Math.floor((Date.now() - puzzleStartTime) / 1000);
    const mins = Math.floor(elapsed / 60)
      .toString()
      .padStart(2, "0");
    const secs = (elapsed % 60).toString().padStart(2, "0");

    let streakHtml = "";
    if (success && puzzleApi().isTodaysDailyPuzzle(puzzle)) {
      const streak = puzzleApi().recordDailyPuzzleSolved();
      streakHtml = `<span>🔥 Streak: <strong>${streak.current}</strong> (Best ${streak.best})</span>`;
    }

    puzzleOverlay.innerHTML = `
      <div class="puzzle-box">
        <div class="puzzle-header">
          <h2 class="puzzle-title">${success ? "🎉 Gelöst!" : "😔 Gescheitert"}</h2>
          <button class="puzzle-close-btn" id="puzzle-result-close" title="Schließen" aria-label="Schließen">✕</button>
        </div>
        <div class="puzzle-result ${success ? "success" : "failed"}">
          <div class="puzzle-result-title">${success ? "Matt gesetzt!" : "Falscher Zug"}</div>
          <div class="puzzle-result-stats">
            <span>Zeit: <strong>${mins}:${secs}</strong></span>
            <span>Züge: <strong>${state.currentMoveIndex}/${puzzle.solution.length}</strong></span>
            ${success && state.hintUsed ? '<span style="color: var(--water)">Hinweis genutzt</span>' : ""}
            ${!success && state.expectedMove ? `<span>Richtig: <strong>${escapeHtml(state.expectedMove.san)}</strong></span>` : ""}
            ${streakHtml}
          </div>
        </div>
        <div class="puzzle-actions">
          <button class="puzzle-btn primary" id="puzzle-again-btn">🔄 Nochmal</button>
          <button class="puzzle-btn secondary" id="puzzle-menu-btn">📋 Menü</button>
        </div>
      </div>
    `;

    document
      .getElementById("puzzle-result-close")
      ?.addEventListener("click", () => {
        cleanupPuzzle();
        showPuzzleMenu();
      });

    document
      .getElementById("puzzle-again-btn")
      ?.addEventListener("click", async () => {
        puzzleApi().resetPuzzle();
        await showPuzzleBoard(puzzle);
      });

    document
      .getElementById("puzzle-menu-btn")
      ?.addEventListener("click", () => {
        cleanupPuzzle();
        showPuzzleMenu();
      });
  }

  function cleanupPuzzle(): void {
    if (puzzleTimerInterval) {
      clearInterval(puzzleTimerInterval);
      puzzleTimerInterval = null;
    }
    puzzleGame = null;
    puzzleRenderer = null;
    puzzleBoardGroup = null;
  }

  async function deserializePuzzleFEN(fen: string): Promise<Game | null> {
    try {
      const [piecesStr, factionIdxStr] = fen.split("#");
      const factionIdx = parseInt(factionIdxStr ?? "0", 10);

      const game = await createGame();
      const cells = generateBoard();
      game.init(cells);

      for (const p of game.pieces) p.alive = false;

      if (piecesStr) {
        const pieceEntries = piecesStr.split("|");
        for (const entry of pieceEntries) {
          if (!entry) continue;
          const factionChar = entry[0];
          const typeChar = entry[1];
          const coords = entry.slice(2).split(",");
          const q = Number(coords[0]);
          const r = Number(coords[1]);

          const faction =
            factionChar === "F"
              ? "fire"
              : factionChar === "W"
                ? "water"
                : "nature";
          const piece = game.pieces.find(
            (p) => p.faction === faction && p.pos.q === q && p.pos.r === r,
          );
          if (piece) piece.alive = true;
        }
      }

      game.currentFactionIdx = factionIdx % 3;
      const factions = ["fire", "water", "nature"] as const;
      game.currentFaction = factions[game.currentFactionIdx] as Faction;
      game._rebuildOccupiedMap();

      return game;
    } catch {
      return null;
    }
  }

  function showLoading(message: string): void {
    puzzleOverlay.innerHTML = `
      <div class="puzzle-box">
        <div class="puzzle-title">${message}</div>
        <div style="margin-top: 1rem; color: var(--water);">⏳</div>
      </div>
    `;
    puzzleOverlay.classList.add("visible");
  }

  function hideLoading(): void {
    // Will be replaced by next screen
  }

  function showError(message: string): void {
    puzzleOverlay.innerHTML = `
      <div class="puzzle-box">
        <div class="puzzle-header">
          <h2 class="puzzle-title" style="color: var(--fire)">⚠️ Fehler</h2>
          <button class="puzzle-close-btn" id="puzzle-error-close" aria-label="Schließen">✕</button>
        </div>
        <p style="color: var(--text-dim); margin: 1rem 0;">${message}</p>
        <button class="puzzle-btn primary" id="puzzle-error-retry">OK</button>
      </div>
    `;
    document
      .getElementById("puzzle-error-close")
      ?.addEventListener("click", () => showPuzzleMenu());
    document
      .getElementById("puzzle-error-retry")
      ?.addEventListener("click", () => showPuzzleMenu());
  }

  function showHintToast(message: string): void {
    const existing = document.getElementById("puzzle-hint-toast");
    if (existing) existing.remove();

    const toast = document.createElement("div");
    toast.id = "puzzle-hint-toast";
    toast.className = "puzzle-hint-toast";
    toast.textContent = message;
    document.body.appendChild(toast);

    requestAnimationFrame(() => toast.classList.add("visible"));

    setTimeout(() => {
      toast.classList.remove("visible");
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }

  // Personality Selector
  const personalitySelect = document.getElementById(
    "personality-select",
  ) as HTMLSelectElement;
  personalitySelect?.addEventListener("change", (e: Event) => {
    const personality = (e.target as HTMLSelectElement).value as
      "balanced" | "aggressive" | "defensive" | "tactical";
    setAIPersonality(personality);
    broadcastPersonality();
    saveSettings({ ...loadSettings(), aiPersonality: personality });
  });

  // Settings Modal
  settingsBtn?.addEventListener("click", () => {
    settingsOverlay?.classList.add("visible");
    // Load current settings into UI
    const settings = loadSettings();
    const autoQueenCheckbox = document.getElementById(
      "auto-queen",
    ) as HTMLInputElement;
    if (autoQueenCheckbox)
      autoQueenCheckbox.checked = settings.autoQueen ?? false;
    const abGames = document.getElementById("ab-games") as HTMLInputElement;
    const abGamesValue = document.getElementById(
      "ab-games-value",
    ) as HTMLElement;
    if (abGames) {
      abGames.value = String(settings.abGames ?? 100);
      if (abGamesValue)
        abGamesValue.textContent = String(settings.abGames ?? 100);
    }
    const abDepth = document.getElementById("ab-depth") as HTMLInputElement;
    const abDepthValue = document.getElementById(
      "ab-depth-value",
    ) as HTMLElement;
    if (abDepth) {
      abDepth.value = String(settings.abDepth ?? 4);
      if (abDepthValue)
        abDepthValue.textContent = String(settings.abDepth ?? 4);
    }
    const abMaxMoves = document.getElementById(
      "ab-max-moves",
    ) as HTMLInputElement;
    const abMaxMovesValue = document.getElementById(
      "ab-max-moves-value",
    ) as HTMLElement;
    if (abMaxMoves) {
      abMaxMoves.value = String(settings.abMaxMoves ?? 300);
      if (abMaxMovesValue)
        abMaxMovesValue.textContent = String(settings.abMaxMoves ?? 300);
    }
    // Personality checkboxes
    const personalities = settings.abPersonalities ?? [
      "balanced",
      "aggressive",
      "defensive",
      "tactical",
    ];
    document
      .querySelectorAll(".personality-checkboxes input[type=checkbox]")
      .forEach((cb) => {
        const checkbox = cb as HTMLInputElement;
        checkbox.checked = personalities.includes(checkbox.value);
      });
  });

  settingsCloseBtn?.addEventListener("click", () => {
    settingsOverlay?.classList.remove("visible");
  });

  settingsOverlay?.addEventListener("click", (e: Event) => {
    if (e.target === settingsOverlay) {
      settingsOverlay.classList.remove("visible");
    }
  });

  // Tab switching
  settingsTabs.forEach((tab) => {
    tab.addEventListener("click", () => {
      const targetTab = tab.getAttribute("data-tab");
      if (!targetTab) return;
      settingsTabs.forEach((t) => t.classList.remove("active"));
      settingsPanels.forEach((p) => p.classList.remove("active"));
      tab.classList.add("active");
      const targetPanel = document.getElementById(`panel-${targetTab}`);
      targetPanel?.classList.add("active");
    });
  });

  // Auto-Queen checkbox
  const autoQueenCheckbox = document.getElementById(
    "auto-queen",
  ) as HTMLInputElement;
  autoQueenCheckbox?.addEventListener("change", (e: Event) => {
    saveSettings({
      ...loadSettings(),
      autoQueen: (e.target as HTMLInputElement).checked,
    });
  });

  // Slider value displays
  const abGames = document.getElementById("ab-games") as HTMLInputElement;
  const abGamesValue = document.getElementById("ab-games-value") as HTMLElement;
  abGames?.addEventListener("input", (e: Event) => {
    const val = (e.target as HTMLInputElement).value;
    if (abGamesValue) abGamesValue.textContent = val;
    saveSettings({ ...loadSettings(), abGames: parseInt(val) });
  });

  const abDepth = document.getElementById("ab-depth") as HTMLInputElement;
  const abDepthValue = document.getElementById("ab-depth-value") as HTMLElement;
  abDepth?.addEventListener("input", (e: Event) => {
    const val = (e.target as HTMLInputElement).value;
    if (abDepthValue) abDepthValue.textContent = val;
    saveSettings({ ...loadSettings(), abDepth: parseInt(val) });
  });

  const abMaxMoves = document.getElementById(
    "ab-max-moves",
  ) as HTMLInputElement;
  const abMaxMovesValue = document.getElementById(
    "ab-max-moves-value",
  ) as HTMLElement;
  abMaxMoves?.addEventListener("input", (e: Event) => {
    const val = (e.target as HTMLInputElement).value;
    if (abMaxMovesValue) abMaxMovesValue.textContent = val;
    saveSettings({ ...loadSettings(), abMaxMoves: parseInt(val) });
  });

  // Personality checkboxes
  document
    .querySelectorAll(".personality-checkboxes input[type=checkbox]")
    .forEach((cb) => {
      cb.addEventListener("change", () => {
        const selected = Array.from(
          document.querySelectorAll(
            ".personality-checkboxes input[type=checkbox]:checked",
          ),
        ).map((c) => (c as HTMLInputElement).value);
        saveSettings({ ...loadSettings(), abPersonalities: selected });
      });
    });

  // Global keyboard shortcuts (when not in input/select)
  document.addEventListener("keydown", (e: KeyboardEvent) => {
    if (
      e.target instanceof HTMLInputElement ||
      e.target instanceof HTMLSelectElement ||
      e.target instanceof HTMLTextAreaElement
    ) {
      return;
    }
    switch (e.key.toLowerCase()) {
      case "d":
        toggleDarkMode();
        break;
      case "escape":
        settingsOverlay?.classList.remove("visible");
        hideTutorial();
        break;
      case "?":
        showHintToast(
          "Shortcuts: ←/→ Nav, Space Play/Pause, F Flip, D Dark, C Copy, ? Help",
        );
        break;
    }
  });

  // Run Auto-Battle Learning button
  const runAbBtn = document.getElementById(
    "run-auto-battle-learning",
  ) as HTMLButtonElement;
  const resumeAbBtn = document.getElementById(
    "resume-auto-battle-learning",
  ) as HTMLButtonElement;
  const abProgress = document.getElementById("ab-progress") as HTMLElement;
  const abProgressFill = document.getElementById(
    "ab-progress-fill",
  ) as HTMLElement;
  const abProgressText = document.getElementById(
    "ab-progress-text",
  ) as HTMLElement;
  const abResult = document.getElementById("ab-result") as HTMLElement;

  // Auto-Battle Learning Resume State
  const LEARN_STATE_KEY = "trischach-auto-battle-learn-state";
  interface LearnState {
    games: number;
    depth: number;
    maxMoves: number;
    personalities: string[];
    completedGames: number;
    stats: { fire: number; water: number; nature: number; draws: number };
    currentGameIdx: number;
    personalitiesOrder: string[];
    isRunning: boolean;
  }

  function saveLearnState(state: LearnState): void {
    try {
      localStorage.setItem(LEARN_STATE_KEY, JSON.stringify(state));
    } catch (e) {
      console.warn("Failed to save learn state:", e);
    }
  }

  function loadLearnState(): LearnState | null {
    try {
      const stored = localStorage.getItem(LEARN_STATE_KEY);
      if (stored) return JSON.parse(stored);
    } catch (e) {
      console.warn("Failed to load learn state:", e);
    }
    return null;
  }

  function clearLearnState(): void {
    try {
      localStorage.removeItem(LEARN_STATE_KEY);
    } catch (e) {
      console.warn("Failed to clear learn state:", e);
    }
  }

  // Check for existing learn state on startup
  const savedState = loadLearnState();
  if (
    savedState &&
    savedState.isRunning &&
    savedState.completedGames < savedState.games
  ) {
    if (resumeAbBtn) resumeAbBtn.style.display = "inline-block";
    if (runAbBtn) runAbBtn.style.display = "none";
  }

  runAbBtn?.addEventListener("click", async () => {
    const settings = loadSettings();
    const games = settings.abGames ?? 100;
    const depth = settings.abDepth ?? 4;
    const maxMoves = settings.abMaxMoves ?? 300;
    const personalities = (settings.abPersonalities ?? [
      "balanced",
      "aggressive",
      "defensive",
      "tactical",
    ]) as string[];

    if (personalities.length === 0) {
      showHintToast("⚠️ Bitte mindestens eine Persönlichkeit wählen!");
      return;
    }

    // Clear any existing state at start
    clearLearnState();

    // Create consistent personality order for this run
    const personalitiesOrder = [] as string[];
    for (let i = 0; i < games; i++) {
      const personality = personalities[i % personalities.length]!;
      personalitiesOrder.push(personality);
    }

    // Show progress
    if (runAbBtn) runAbBtn.disabled = true;
    if (abProgress) abProgress.style.display = "block";
    if (abProgressFill) abProgressFill.style.width = "0%";
    if (abProgressText) abProgressText.textContent = "Initialisiere...";
    if (abResult) {
      abResult.style.display = "none";
      abResult.textContent = "";
    }

    try {
      const { calculateBestMove, setAIDepth, setAIPersonality } =
        await import("./ai-core.ts");
      const { Game } = await import("./game.ts");
      const { generateBoard } = await import("./board.ts");
      const { learnFromGame, getLearnedData } =
        await import("./opening-book.ts");

      setAIDepth(depth);

      const stats = { fire: 0, water: 0, nature: 0, draws: 0 };
      let totalGames = 0;

      // Helper to play one game
      function playGame(gameIdx: number): Promise<void> {
        return new Promise((resolve) => {
          const game = new Game();
          const cells = generateBoard();
          game.init(cells);
          game.rpsEnabled = true;

          const personality = (personalitiesOrder[
            gameIdx % personalitiesOrder.length
          ] ?? "balanced") as
            "balanced" | "aggressive" | "defensive" | "tactical";
          setAIPersonality(personality);

          const gameHistory: Array<{
            hash: string;
            faction: Faction;
            move: { pieceId: string; targetQ: number; targetR: number };
          }> = [];
          let moveCount = 0;

          function makeMove() {
            if (game.state === "game_over" || moveCount >= maxMoves) {
              // Learn from this game
              let winnerFaction: "fire" | "water" | "nature" | null = null;
              if (game.state === "game_over") {
                const alive = game.pieces.filter((p: Piece) => p.alive);
                if (alive.length === 1) {
                  const winner = alive[0];
                  if (winner) {
                    winnerFaction = winner.faction as
                      "fire" | "water" | "nature";
                    stats[winnerFaction]++;
                  } else {
                    stats.draws++;
                  }
                } else {
                  stats.draws++;
                }
              } else {
                stats.draws++;
              }

              if (gameHistory.length > 0) {
                learnFromGame(gameHistory, winnerFaction);
              }

              totalGames++;
              const pct = Math.round((totalGames / games) * 100);
              if (abProgressFill) abProgressFill.style.width = `${pct}%`;
              if (abProgressText)
                abProgressText.textContent = `Spiel ${totalGames} / ${games} (${pct}%)`;

              // Save state after each game for resume capability
              const state: LearnState = {
                games,
                depth,
                maxMoves,
                personalities,
                completedGames: totalGames,
                stats,
                currentGameIdx: gameIdx,
                personalitiesOrder,
                isRunning: true,
              };
              saveLearnState(state);

              resolve();
              return;
            }

            const action = calculateBestMove(game, game.currentFaction);
            if (!action) {
              resolve();
              return;
            }

            if (moveCount < 30) {
              const hash = boardHash(game);
              gameHistory.push({
                hash,
                faction: game.currentFaction,
                move: {
                  pieceId: action.piece.id,
                  targetQ: action.target.q,
                  targetR: action.target.r,
                },
              });
            }

            const selResult = game.handleCellClick(action.piece.pos);
            if (selResult?.action === "select") {
              const result = game.handleCellClick(action.target);
              if (result?.promotion && game.pendingPromotion) {
                game.completePromotion("queen");
              }
            }

            moveCount++;
            setTimeout(makeMove, 0);
          }

          makeMove();
        });
      }

      function boardHash(g: IGame): string {
        const pieces = g.pieces
          .filter((p: Piece) => p.alive)
          .map((p: Piece) => `${p.faction[0]}${p.type[0]}${p.pos.q},${p.pos.r}`)
          .sort()
          .join("|");
        return `${pieces}#${g.currentFactionIdx}`;
      }

      // Run games sequentially with small batches for UI responsiveness
      for (let i = 0; i < games; i++) {
        await playGame(i);
      }

      // Save learned data
      const learnedData = getLearnedData();
      const saveData = {
        version: 1,
        updated: new Date().toISOString(),
        positions: learnedData,
      };
      localStorage.setItem(
        "trischach-opening-book-learned",
        JSON.stringify(saveData),
      );

      clearLearnState();

      // Also try to update the compiled book (optional, requires reload)
      // For now, just show success
      if (abProgress) abProgress.style.display = "none";
      if (abResult) {
        abResult.style.display = "block";
        abResult.textContent =
          `✅ Fertig! ${games} Spiele gespielt.\\n` +
          `🔥 Feuer: ${stats.fire} | 🌊 Wasser: ${stats.water} | 🌿 Natur: ${stats.nature} | 🤝 Unentschieden: ${stats.draws}\\n` +
          `📚 Gelernte Positionen: ${Object.keys(learnedData).length}\\n` +
          `💾 In localStorage gespeichert. Beim nächsten Spielstart aktiv.`;
      }
      showHintToast(`✅ Auto-Battle Learning abgeschlossen (${games} Spiele)`);
    } catch (err) {
      console.error("Auto-Battle Learning failed:", err);
      if (abProgress) abProgress.style.display = "none";
      if (abResult) {
        abResult.style.display = "block";
        abResult.textContent = `❌ Fehler: ${err instanceof Error ? err.message : String(err)}`;
      }
      showHintToast("❌ Auto-Battle Learning fehlgeschlagen");
      clearLearnState();
    } finally {
      if (runAbBtn) runAbBtn.disabled = false;
    }
  });

  // Resume Auto-Battle Learning button
  resumeAbBtn?.addEventListener("click", async () => {
    const savedState = loadLearnState();
    if (
      !savedState ||
      !savedState.isRunning ||
      savedState.completedGames >= savedState.games
    ) {
      showHintToast("⚠️ Kein State zum Fortsetzen gefunden");
      return;
    }

    const settings = loadSettings();
    const games = savedState.games;
    const depth = savedState.depth;
    const maxMoves = savedState.maxMoves;
    const personalities = savedState.personalities;
    const personalitiesOrder = savedState.personalitiesOrder;
    const stats = savedState.stats;
    let totalGames = savedState.completedGames;

    if (personalities.length === 0) {
      showHintToast("⚠️ Keine Persönlichkeiten konfiguriert!");
      return;
    }

    // Show progress
    if (resumeAbBtn) resumeAbBtn.disabled = true;
    if (runAbBtn) runAbBtn.style.display = "none";
    if (resumeAbBtn) resumeAbBtn.style.display = "none";
    if (abProgress) abProgress.style.display = "block";
    if (abProgressFill)
      abProgressFill.style.width = `${Math.round((totalGames / games) * 100)}%`;
    if (abProgressText)
      abProgressText.textContent = `Spiel ${totalGames} / ${games} (${Math.round((totalGames / games) * 100)}%)`;
    if (abResult) {
      abResult.style.display = "none";
      abResult.textContent = "";
    }

    try {
      const { calculateBestMove, setAIDepth, setAIPersonality } =
        await import("./ai-core.ts");
      const { Game } = await import("./game.ts");
      const { generateBoard } = await import("./board.ts");
      const { learnFromGame, getLearnedData } =
        await import("./opening-book.ts");

      setAIDepth(depth);

      // Helper to play one game
      function playGame(gameIdx: number): Promise<void> {
        return new Promise((resolve) => {
          const game = new Game();
          const cells = generateBoard();
          game.init(cells);
          game.rpsEnabled = true;

          const personality = (personalitiesOrder[
            gameIdx % personalitiesOrder.length
          ] ?? "balanced") as
            "balanced" | "aggressive" | "defensive" | "tactical";
          setAIPersonality(personality);

          const gameHistory: Array<{
            hash: string;
            faction: Faction;
            move: { pieceId: string; targetQ: number; targetR: number };
          }> = [];
          let moveCount = 0;

          function makeMove() {
            if (game.state === "game_over" || moveCount >= maxMoves) {
              // Learn from this game
              let winnerFaction: "fire" | "water" | "nature" | null = null;
              if (game.state === "game_over") {
                const alive = game.pieces.filter((p: Piece) => p.alive);
                if (alive.length === 1) {
                  const winner = alive[0];
                  if (winner) {
                    winnerFaction = winner.faction as
                      "fire" | "water" | "nature";
                    stats[winnerFaction]++;
                  } else {
                    stats.draws++;
                  }
                } else {
                  stats.draws++;
                }
              } else {
                stats.draws++;
              }

              if (gameHistory.length > 0) {
                learnFromGame(gameHistory, winnerFaction);
              }

              totalGames++;
              const pct = Math.round((totalGames / games) * 100);
              if (abProgressFill) abProgressFill.style.width = `${pct}%`;
              if (abProgressText)
                abProgressText.textContent = `Spiel ${totalGames} / ${games} (${pct}%)`;

              // Save state after each game
              const state: LearnState = {
                games,
                depth,
                maxMoves,
                personalities,
                completedGames: totalGames,
                stats,
                currentGameIdx: gameIdx,
                personalitiesOrder,
                isRunning: true,
              };
              saveLearnState(state);

              resolve();
              return;
            }

            const action = calculateBestMove(game, game.currentFaction);
            if (!action) {
              resolve();
              return;
            }

            if (moveCount < 30) {
              const hash = boardHash(game);
              gameHistory.push({
                hash,
                faction: game.currentFaction,
                move: {
                  pieceId: action.piece.id,
                  targetQ: action.target.q,
                  targetR: action.target.r,
                },
              });
            }

            const selResult = game.handleCellClick(action.piece.pos);
            if (selResult?.action === "select") {
              const result = game.handleCellClick(action.target);
              if (result?.promotion && game.pendingPromotion) {
                game.completePromotion("queen");
              }
            }

            moveCount++;
            setTimeout(makeMove, 0);
          }

          makeMove();
        });
      }

      function boardHash(g: IGame): string {
        const pieces = g.pieces
          .filter((p: Piece) => p.alive)
          .map((p: Piece) => `${p.faction[0]}${p.type[0]}${p.pos.q},${p.pos.r}`)
          .sort()
          .join("|");
        return `${pieces}#${g.currentFactionIdx}`;
      }

      // Run remaining games
      for (let i = totalGames; i < games; i++) {
        await playGame(i);
      }

      // Save learned data
      const learnedData = getLearnedData();
      const saveData = {
        version: 1,
        updated: new Date().toISOString(),
        positions: learnedData,
      };
      localStorage.setItem(
        "trischach-opening-book-learned",
        JSON.stringify(saveData),
      );

      clearLearnState();

      if (abProgress) abProgress.style.display = "none";
      if (abResult) {
        abResult.style.display = "block";
        abResult.textContent =
          `✅ Fortgesetzt & abgeschlossen! ${games} Spiele insgesamt.\\n` +
          `🔥 Feuer: ${stats.fire} | 🌊 Wasser: ${stats.water} | 🌿 Natur: ${stats.nature} | 🤝 Unentschieden: ${stats.draws}\\n` +
          `📚 Gelernte Positionen: ${Object.keys(learnedData).length}\\n` +
          `💾 In localStorage gespeichert. Beim nächsten Spielstart aktiv.`;
      }
      showHintToast(
        `✅ Auto-Battle Learning fortgesetzt & abgeschlossen (${games} Spiele)`,
      );

      if (resumeAbBtn) resumeAbBtn.style.display = "none";
      if (runAbBtn) runAbBtn.style.display = "inline-block";
    } catch (err) {
      console.error("Auto-Battle Learning resume failed:", err);
      if (abProgress) abProgress.style.display = "none";
      if (abResult) {
        abResult.style.display = "block";
        abResult.textContent = `❌ Fehler: ${err instanceof Error ? err.message : String(err)}`;
      }
      showHintToast("❌ Auto-Battle Learning Fortsetzen fehlgeschlagen");
    } finally {
      if (resumeAbBtn) resumeAbBtn.disabled = false;
    }
  });

  // Opening Book Tab Logic
  const obRefresh = document.getElementById("ob-refresh") as HTMLButtonElement;
  const obExportLearned = document.getElementById(
    "ob-export-learned",
  ) as HTMLButtonElement;
  const obResetLearned = document.getElementById(
    "ob-reset-learned",
  ) as HTMLButtonElement;
  const obPositions = document.getElementById("ob-positions") as HTMLElement;
  const obVariations = document.getElementById("ob-variations") as HTMLElement;
  const obMaxDepth = document.getElementById("ob-max-depth") as HTMLElement;
  const obLearned = document.getElementById("ob-learned") as HTMLElement;
  const obCompiled = document.getElementById("ob-compiled") as HTMLElement;
  const obUpdated = document.getElementById("ob-updated") as HTMLElement;
  // Load opening book stats
  async function loadOpeningBookStats() {
    try {
      // Load compiled book info
      const module = await import("../opening-book.compiled.json");
      const data = module.default;

      if (data && data.metadata && data.book) {
        if (obPositions)
          obPositions.textContent = String(
            data.metadata.stats?.totalPositions ||
              Object.keys(data.book).length,
          );
        if (obVariations)
          obVariations.textContent = String(
            data.metadata.stats?.totalVariations || "\u2013",
          );
        if (obMaxDepth)
          obMaxDepth.textContent = String(
            data.metadata.stats?.maxDepth || "\u2013",
          );
        if (obCompiled)
          obCompiled.textContent = data.metadata.compiled
            ? new Date(data.metadata.compiled).toLocaleString("de-DE")
            : "\u2013";
      }

      // Load learned data from localStorage
      const learnedStored = localStorage.getItem(
        "trischach-opening-book-learned",
      );
      if (learnedStored) {
        try {
          const learnedData = JSON.parse(learnedStored);
          if (learnedData && learnedData.positions) {
            if (obLearned)
              obLearned.textContent = String(
                Object.keys(learnedData.positions).length,
              );
            if (obUpdated)
              obUpdated.textContent = learnedData.updated
                ? new Date(learnedData.updated).toLocaleString("de-DE")
                : "\u2013";
          }
        } catch {
          if (obLearned) obLearned.textContent = "0";
        }
      } else {
        if (obLearned) obLearned.textContent = "0";
        if (obUpdated) obUpdated.textContent = "\u2013";
      }
    } catch (err) {
      console.error("Failed to load opening book stats:", err);
      if (obPositions) obPositions.textContent = "Fehler";
      if (obVariations) obVariations.textContent = "Fehler";
      if (obMaxDepth) obMaxDepth.textContent = "Fehler";
      if (obLearned) obLearned.textContent = "Fehler";
      if (obCompiled) obCompiled.textContent = "Fehler";
      if (obUpdated) obUpdated.textContent = "Fehler";
    }
  }

  // Refresh button
  obRefresh?.addEventListener("click", async () => {
    showHintToast("🔄 Lade Opening Book Statistiken...");
    await loadOpeningBookStats();
    showHintToast("✅ Statistiken aktualisiert");
  });

  // Export learned data
  obExportLearned?.addEventListener("click", () => {
    const learnedStored = localStorage.getItem(
      "trischach-opening-book-learned",
    );
    if (!learnedStored) {
      showHintToast("⚠️ Keine gelernten Daten vorhanden");
      return;
    }
    try {
      const blob = new Blob([learnedStored], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `trischach-learned-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      showHintToast("📥 Gelernte Daten exportiert");
    } catch (err) {
      console.error("Export failed:", err);
      showHintToast("❌ Export fehlgeschlagen");
    }
  });

  // Reset learned data
  obResetLearned?.addEventListener("click", () => {
    if (
      !confirm(
        "⚠️ Alle gelernten Opening Book Daten wirklich löschen?\nDies kann nicht rückgängig gemacht werden.",
      )
    ) {
      return;
    }
    localStorage.removeItem("trischach-opening-book-learned");
    if (obLearned) obLearned.textContent = "0";
    if (obUpdated) obUpdated.textContent = "–";
    showHintToast("🗑️ Learning zurückgesetzt");
  });

  // Initial load when Opening Book tab is opened
  // We'll use a mutation observer or just load on tab click
  const obTab = document.querySelector(
    '.settings-tab[data-tab="opening-book"]',
  ) as HTMLButtonElement;
  obTab?.addEventListener("click", () => {
    loadOpeningBookStats();
  });

  // Dark Mode Toggle
  function loadDarkModePreference(): void {
    const saved = localStorage.getItem("trischach-dark-mode");
    if (saved === "light") {
      document.documentElement.setAttribute("data-theme", "light");
      darkModeBtn?.classList.add("active");
    } else {
      document.documentElement.removeAttribute("data-theme");
      darkModeBtn?.classList.remove("active");
    }
  }

  function toggleDarkMode(): void {
    const isLight =
      document.documentElement.getAttribute("data-theme") === "light";
    if (isLight) {
      document.documentElement.removeAttribute("data-theme");
      localStorage.setItem("trischach-dark-mode", "dark");
      darkModeBtn?.classList.remove("active");
    } else {
      document.documentElement.setAttribute("data-theme", "light");
      localStorage.setItem("trischach-dark-mode", "light");
      darkModeBtn?.classList.add("active");
    }
  }

  darkModeBtn?.addEventListener("click", () => {
    toggleDarkMode();
  });

  // Load dark mode preference on startup
  loadDarkModePreference();

  // ─── Skin (faction colours) ──────────────────────────────────────
  const skinSelect = document.getElementById(
    "skin-select",
  ) as HTMLSelectElement | null;

  function refreshPieceColours(): void {
    // Re-render every piece so inline colour styles (from FACTION_COLORS)
    // pick up the new skin. Redraw both the main board and, if active, the
    // puzzle board.
    for (const p of game.getAlivePieces()) renderer.renderPiece(p);
    updateUI();
  }

  function applyAndRenderSkin(id: string): void {
    applySkin(id);
    refreshPieceColours();
  }

  if (skinSelect) {
    skinSelect.addEventListener("change", () => {
      const id = SKINS[skinSelect.value] ? skinSelect.value : "default";
      saveSkinId(id);
      applyAndRenderSkin(id);
    });
  }

  // Apply persisted skin on startup and sync the selector.
  const initialSkin = loadSkinId();
  if (skinSelect) skinSelect.value = initialSkin;
  applyAndRenderSkin(initialSkin);

  // We can also just load once when initEventListeners runs
  loadOpeningBookStats();

  // ─── Tutorial ─────────────────────────────────────────────────────
  let tutorialStepIndex = 0;

  function hideTutorial(): void {
    if (!tutorialOverlay) return;
    tutorialOverlay.classList.remove("visible");
    tutorialOverlay.setAttribute("aria-hidden", "true");
    tutorialOverlay.innerHTML = "";
  }

  function finishTutorial(): void {
    markTutorialDone();
    hideTutorial();
  }

  function renderTutorialStep(index: number): void {
    if (!tutorialOverlay) return;
    const steps = getTutorialSteps();
    const step = steps[index];
    if (!step) {
      finishTutorial();
      return;
    }
    tutorialStepIndex = index;
    const isLast = index >= steps.length - 1;
    tutorialOverlay.innerHTML = `
      <div class="tutorial-box" role="dialog" aria-modal="true" aria-labelledby="tutorial-title">
        <div class="tutorial-icon">${step.icon}</div>
        <h2 class="tutorial-title" id="tutorial-title">${escapeHtml(step.title)}</h2>
        <p class="tutorial-body">${escapeHtml(step.body)}</p>
        <ul class="tutorial-bullets">
          ${step.bullets.map((b) => `<li>${escapeHtml(b)}</li>`).join("")}
        </ul>
        <div class="tutorial-progress" aria-hidden="true">
          ${steps
            .map(
              (_, i) =>
                `<span class="tutorial-dot${i === index ? " active" : ""}"></span>`,
            )
            .join("")}
        </div>
        <div class="tutorial-actions">
          <button type="button" class="tutorial-btn ghost" id="tutorial-skip">Überspringen</button>
          ${
            index > 0
              ? `<button type="button" class="tutorial-btn" id="tutorial-back">Zurück</button>`
              : ""
          }
          <button type="button" class="tutorial-btn primary" id="tutorial-next">
            ${isLast ? "Los geht's" : "Weiter"}
          </button>
        </div>
      </div>
    `;
    tutorialOverlay.classList.add("visible");
    tutorialOverlay.setAttribute("aria-hidden", "false");

    document
      .getElementById("tutorial-skip")
      ?.addEventListener("click", () => finishTutorial());
    document
      .getElementById("tutorial-back")
      ?.addEventListener("click", () => renderTutorialStep(index - 1));
    document.getElementById("tutorial-next")?.addEventListener("click", () => {
      if (isLast) finishTutorial();
      else renderTutorialStep(index + 1);
    });
  }

  function showTutorial(fromStart = true): void {
    if (fromStart) tutorialStepIndex = 0;
    renderTutorialStep(tutorialStepIndex);
  }

  document
    .getElementById("tutorial-btn")
    ?.addEventListener("click", () => showTutorial(true));
  document
    .getElementById("tutorial-replay-btn")
    ?.addEventListener("click", () => {
      resetTutorial();
      // Close settings so the tutorial is visible above the board.
      settingsOverlay?.classList.remove("visible");
      showTutorial(true);
    });

  if (shouldShowTutorialOnStartup()) {
    // Slight delay so the board paints first.
    setTimeout(() => showTutorial(true), 400);
  }
}

// ─── Lazy-Module-Chunks (nicht in main.js) ─────────────────────────────
export async function loadRpsPuzzleModule(): Promise<
  typeof import("./rps-puzzle.ts")
> {
  rpsPuzzleModule ??= await retryImport(() => import("./rps-puzzle.ts"));
  return rpsPuzzleModule;
}

/** Synchroner Zugriff auf das bereits geladene RPS-Puzzle-Modul. */
export function rpsPuzzleApi(): typeof import("./rps-puzzle.ts") {
  if (!rpsPuzzleModule) {
    throw new Error(
      "RPS-Puzzle-Modul noch nicht geladen – zuerst loadRpsPuzzleModule() aufrufen.",
    );
  }
  return rpsPuzzleModule;
}

export async function loadCoachModule(): Promise<typeof import("./coach.ts")> {
  coachModule ??= await retryImport(() => import("./coach.ts"));
  return coachModule;
}

/** Synchroner Zugriff auf das bereits geladene Coach-Modul. */
export function coachApi(): typeof import("./coach.ts") {
  if (!coachModule) {
    throw new Error(
      "Coach-Modul noch nicht geladen – zuerst loadCoachModule() aufrufen.",
    );
  }
  return coachModule;
}

// ─── Start ──────────────────────────────────────────────────────────

function initApp(): void {
  initEventListeners();
  init();
  prefetchLazyModes();
}

/**
 * Lädt die Modul-Chunks der Modi (Puzzle, Replay) im Hintergrund nach.
 *
 * Sie liegen bewusst außerhalb von main.js: das hält den kritischen Pfad klein
 * (Bundle-Gate, schnellerer First Paint). Ein Fetch erst beim Klick trifft den
 * Dev-Server unter Last und scheitert dort sporadisch ("Failed to fetch
 * dynamically imported module") — dann bleibt z. B. das Puzzle-Menü leer.
 * Deshalb wird direkt nach dem Start vorgeladen; der Fehler wird geschluckt,
 * der Klick lädt notfalls selbst nach (siehe retryImport).
 *
 * analysis.ts bleibt absichtlich ungeladen — der Analyse-Modus ist eine
 * seltene Aktion aus dem Replay-Panel heraus.
 */
function prefetchLazyModes(): void {
  const ignore = () => {
    /* bewusst still: der Aufruf lädt bei Bedarf erneut */
  };
  void loadPuzzleModule().catch(ignore);
  void loadReplayModule().catch(ignore);
  void loadCoachModule().catch(ignore);
  void loadRpsPuzzleModule().catch(ignore);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initApp);
} else {
  initApp();
}

export { game, renderer, triggerAutoMove, isAutoBattleActive };
